import {
  Bold,
  Check,
  CircleHelp,
  CircleCheck,
  CalendarPlus,
  CalendarX2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Code2,
  FolderClosed,
  FolderOpen,
  FolderPlus,
  FolderTree,
  Highlighter,
  Italic,
  ListPlus,
  Ellipsis,
  EyeOff,
  Monitor,
  Moon,
  Palette,
  Pencil,
  PanelLeft,
  PanelRight,
  Plus,
  Search,
  Strikethrough,
  Sun,
  Tags as TagsIcon,
  Trash2,
  Undo2,
  X
} from "lucide-react";
import { WorkspaceIcon, randomWorkspaceIcon } from "./workspaceIcons";
import { useVirtualizer } from "@tanstack/react-virtual";
import ReactMarkdown from "react-markdown";
import { createPortal } from "react-dom";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import remarkGfm from "remark-gfm";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent
} from "react";
import {
  apiDelete,
  apiGet,
  apiPatch,
  apiPost,
  type OutlineHistoryResult,
  type OutlineHistoryState,
  type OutlineNode,
  type OutlineTreeNode,
  type RecycleBinEntry,
  type Tag,
  type TaggedNodeGroup,
  type TaggedNodeResult,
  type Workspace,
  type WorkspaceFolder
} from "./api";
import { useTheme, type Theme } from "./theme";
import { getDueReminderCutoff, getDueReminderNodes } from "./dueTasks";
import { SyncPanel } from "./SyncPanel";
import { activatePrivacyScreen } from "./PrivacyScreen";
import { useInlineTagInput } from "./InlineTagInput";
import { beginNodeEdit, endNodeEdit, flushNodeDraft, readNodeDraft, stageNodeDraft } from "./offline";
import {
  type FlatTreeState,
  type FlatNodeData,
  fromNestedTree,
  computeVisibleIds,
  searchNodeIds,
  updateNode,
  insertNode,
  removeNode,
  removeNodes,
  replaceNode,
  moveNode,
  moveNodes,
  getTopLevelNodeIds,
  getIndentTargetId,
  getNode,
  getParentId,
  isDescendant,
  hasNode
} from "./flatTree";

/** Compute depth in O(depth) by walking the parent chain. */
function getNodeDepth(state: FlatTreeState, id: string): number {
  let depth = 0;
  let current = state.nodes[id];
  while (current?.parentId && current.parentId !== state.rootId) {
    depth++;
    current = state.nodes[current.parentId];
  }
  return depth;
}

export function revealNodeInFlatTree(
  state: FlatTreeState,
  nodeId: string
): { state: FlatTreeState; visibleIds: string[]; index: number } {
  if (!hasNode(state, nodeId)) return { state, visibleIds: computeVisibleIds(state), index: -1 };

  let next = state;
  let parentId = getParentId(next, nodeId);
  const visited = new Set<string>();
  while (parentId && !visited.has(parentId)) {
    visited.add(parentId);
    const parent = getNode(next, parentId);
    if (!parent) break;
    if (parent.collapsed) {
      if (next === state) next = { ...state, nodes: { ...state.nodes } };
      next.nodes[parentId] = { ...parent, collapsed: false };
    }
    parentId = parent.parentId;
  }

  const visibleIds = computeVisibleIds(next);
  return { state: next, visibleIds, index: visibleIds.indexOf(nodeId) };
}

export function getChildCountLabel(childCount: number): string | null {
  return childCount > 0 ? String(childCount) : null;
}

export function formatNodeDate(value: string): string {
  return value.replaceAll("-", "/");
}

export function getNodeSelectionRange(visibleIds: string[], anchorId: string, targetId: string): string[] {
  const targetIndex = visibleIds.indexOf(targetId);
  if (targetIndex < 0) return [];
  const anchorIndex = visibleIds.indexOf(anchorId);
  if (anchorIndex < 0) return [targetId];
  const start = Math.min(anchorIndex, targetIndex);
  const end = Math.max(anchorIndex, targetIndex);
  return visibleIds.slice(start, end + 1);
}

export type NodeSelectionPosition = "single" | "start" | "middle" | "end";

export function getNodeSelectionPosition(
  visibleIds: string[],
  selectedIds: ReadonlySet<string>,
  index: number
): NodeSelectionPosition | null {
  const nodeId = visibleIds[index];
  if (!nodeId || !selectedIds.has(nodeId)) return null;
  const previousSelected = index > 0 && selectedIds.has(visibleIds[index - 1]);
  const nextSelected = index < visibleIds.length - 1 && selectedIds.has(visibleIds[index + 1]);
  if (previousSelected && nextSelected) return "middle";
  if (previousSelected) return "end";
  if (nextSelected) return "start";
  return "single";
}

interface LoadTreeOptions {
  preserveSelection?: boolean;
}

type DropPlacement = "before" | "inside" | "after";
type WorkspaceDropPlacement = "before" | "inside" | "after";
export type MarkdownStyle = "bold" | "italic" | "strike" | "code" | "highlight";

export const MARKDOWN_TEXT_COLORS = [
  { id: "red", label: "Red" },
  { id: "orange", label: "Orange" },
  { id: "yellow", label: "Yellow" },
  { id: "green", label: "Green" },
  { id: "blue", label: "Blue" },
  { id: "purple", label: "Purple" },
  { id: "gray", label: "Gray" }
] as const;

export type MarkdownTextColor = (typeof MARKDOWN_TEXT_COLORS)[number]["id"];

interface MarkdownContextMenuState {
  x: number;
  y: number;
  selectionStart: number;
  selectionEnd: number;
  colorPaletteOpen: boolean;
}

interface NodeContextMenuState {
  x: number;
  y: number;
}

interface WorkspaceDragTarget {
  folderId: string | null;
  parentWorkspaceId: string | null;
  position: number;
  markerId: string;
  overWorkspaceId?: string;
  placement?: WorkspaceDropPlacement;
}

interface DragState {
  draggingIds: string[];
  movingIds: string[];
  title: string;
  x: number;
  y: number;
  overId?: string;
  placement?: DropPlacement;
  overWorkspaceId?: string;
}

type NodeDragTarget =
  | { kind: "node"; overId: string; placement: DropPlacement }
  | { kind: "workspace"; workspaceId: string };

interface NodeSelectionDrag {
  pointerId: number;
  anchorId: string;
  startX: number;
  startY: number;
  additive: boolean;
  baseSelection: Set<string>;
  moved: boolean;
}

export type SystemTagRow =
  | { kind: "tag"; group: TaggedNodeGroup }
  | { kind: "node"; groupName: string; color: string; result: TaggedNodeResult };

interface TitleSelection {
  start: number;
  end: number;
}

type AfterCompositionEnd = (callback: () => void) => void;

export class NodeCompositionTracker {
  private readonly composingNodeIds = new Set<string>();
  private readonly waiters = new Map<string, Set<() => void>>();

  constructor(private readonly afterCompositionEnd: AfterCompositionEnd) {}

  start(nodeId: string) {
    this.composingNodeIds.add(nodeId);
  }

  finish(nodeId: string) {
    if (!this.composingNodeIds.delete(nodeId)) return;
    const pending = this.waiters.get(nodeId);
    this.waiters.delete(nodeId);
    for (const resolve of pending ?? []) resolve();
  }

  async wait(nodeId: string): Promise<void> {
    if (!this.composingNodeIds.has(nodeId)) return;
    await new Promise<void>(resolve => {
      const pending = this.waiters.get(nodeId) ?? new Set<() => void>();
      pending.add(resolve);
      this.waiters.set(nodeId, pending);
    });
    await new Promise<void>(resolve => this.afterCompositionEnd(resolve));
    await this.wait(nodeId);
  }
}

interface PendingDelete {
  selectedIds: string[];
  primaryId: string;
  anchorId: string;
  nodeCount: number;
  workspaceId: string;
  focusAfterDeleteId: string;
  createdAt: number;
  action: "delete" | "complete";
}

interface ConvertWorkspaceCandidate {
  id: string;
  title: string;
}

type ResizablePanel = "sidebar" | "inspector";

const DEFAULT_SIDEBAR_WIDTH = 264;
const MIN_SIDEBAR_WIDTH = 200;
const MAX_SIDEBAR_WIDTH = 420;
const DEFAULT_INSPECTOR_WIDTH = 264;
const MIN_INSPECTOR_WIDTH = 220;
const MAX_INSPECTOR_WIDTH = 480;
const PANEL_RESIZE_STEP = 16;
const SIDEBAR_COLLAPSED_STORAGE_KEY = "openoutliner.sidebar-collapsed:v1";
const COLLAPSED_WORKSPACE_FOLDERS_STORAGE_KEY = "openoutliner.collapsed-workspace-folders:v1";
const COLLAPSED_WORKSPACES_STORAGE_KEY = "openoutliner.collapsed-workspaces:v1";
const COLLAPSED_SYSTEM_TAGS_STORAGE_KEY = "openoutliner.collapsed-system-tags:v1";
const SIDEBAR_WIDTH_STORAGE_KEY = "openoutliner.sidebar-width";
const INSPECTOR_WIDTH_STORAGE_KEY = "openoutliner.inspector-width";
export const SYSTEM_TAGS_WORKSPACE_ID = "system:tags";
export const SYSTEM_RECYCLE_BIN_WORKSPACE_ID = "system:recycle-bin";
const EMPTY_OUTLINE_HISTORY: OutlineHistoryState = {
  canUndo: false,
  canRedo: false,
  undoLabel: null,
  redoLabel: null
};

const markdownTextColorIds = new Set<string>(MARKDOWN_TEXT_COLORS.map(color => color.id));
const markdownTextColorClassNames = MARKDOWN_TEXT_COLORS.map(color => `markdownTextColor-${color.id}`);
const markdownSanitizeSchema = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames ?? []), "mark", "span"],
  attributes: {
    ...defaultSchema.attributes,
    span: [...(defaultSchema.attributes?.span ?? []), ["className", ...markdownTextColorClassNames]]
  }
};

export function App() {
  const { theme, setTheme } = useTheme();
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [workspaceFolders, setWorkspaceFolders] = useState<WorkspaceFolder[]>([]);
  const [workspaceId, setWorkspaceId] = useState<string>("");
  const [flatState, setFlatState] = useState<FlatTreeState | null>(null);
  const [visibleIds, setVisibleIds] = useState<string[]>([]);
  const [selectedId, setSelectedId] = useState<string>("");
  const [selectedNodeIds, setSelectedNodeIds] = useState<Set<string>>(() => new Set());
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [tags, setTags] = useState<Tag[]>([]);
  const [activeTagFilter, setActiveTagFilter] = useState("");
  const [tagResults, setTagResults] = useState<TaggedNodeResult[]>([]);
  const [systemTagGroups, setSystemTagGroups] = useState<TaggedNodeGroup[]>([]);
  const [recycleBinEntries, setRecycleBinEntries] = useState<RecycleBinEntry[]>([]);
  const [restoringRecycleBinIds, setRestoringRecycleBinIds] = useState<Set<string>>(() => new Set());
  const [emptyingRecycleBin, setEmptyingRecycleBin] = useState(false);
  const [collapsedSystemTags, setCollapsedSystemTags] = useState<Set<string>>(() =>
    readStoredIdSet(COLLAPSED_SYSTEM_TAGS_STORAGE_KEY)
  );
  const [isInspectorOpen, setIsInspectorOpen] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() =>
    readStoredBoolean(SIDEBAR_COLLAPSED_STORAGE_KEY, false)
  );
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const sidebarCompact = sidebarCollapsed && !mobileSidebarOpen;
  const sidebarSearchRef = useRef<HTMLInputElement | null>(null);
  const [sidebarWidth, setSidebarWidth] = useState(() =>
    readStoredPanelWidth(SIDEBAR_WIDTH_STORAGE_KEY, DEFAULT_SIDEBAR_WIDTH, MIN_SIDEBAR_WIDTH, MAX_SIDEBAR_WIDTH)
  );
  const [inspectorWidth, setInspectorWidth] = useState(() =>
    readStoredPanelWidth(
      INSPECTOR_WIDTH_STORAGE_KEY,
      DEFAULT_INSPECTOR_WIDTH,
      MIN_INSPECTOR_WIDTH,
      MAX_INSPECTOR_WIDTH
    )
  );
  const [isMarkdownHelpOpen, setIsMarkdownHelpOpen] = useState(false);
  const [workspaceDragTarget, setWorkspaceDragTarget] = useState<WorkspaceDragTarget | null>(null);
  const [collapsedWorkspaceFolderIds, setCollapsedWorkspaceFolderIds] = useState<Set<string>>(() =>
    readStoredIdSet(COLLAPSED_WORKSPACE_FOLDERS_STORAGE_KEY)
  );
  const [collapsedWorkspaceIds, setCollapsedWorkspaceIds] = useState<Set<string>>(() =>
    readStoredIdSet(COLLAPSED_WORKSPACES_STORAGE_KEY)
  );
  const [dragState, setDragState] = useState<DragState | null>(null);
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);
  const [outlineHistory, setOutlineHistory] = useState<OutlineHistoryState>(EMPTY_OUTLINE_HISTORY);
  const [convertWorkspaceCandidate, setConvertWorkspaceCandidate] = useState<ConvertWorkspaceCandidate | null>(null);
  const [isConvertingWorkspace, setIsConvertingWorkspace] = useState(false);
  const [isMovingToWorkspace, setIsMovingToWorkspace] = useState(false);
  const workspaceIdRef = useRef("");
  const pendingWorkspaceFocusIdRef = useRef("");
  const treeRequestRef = useRef(0);
  const tagsRequestRef = useRef(0);
  const tagResultsRequestRef = useRef(0);
  const systemTagGroupsRequestRef = useRef(0);
  const recycleBinRequestRef = useRef(0);
  const outlineHistoryRequestRef = useRef(0);
  const dragTargetRef = useRef<NodeDragTarget | null>(null);
  const workspaceDragTargetRef = useRef<WorkspaceDragTarget | null>(null);
  const selectedIdRef = useRef("");
  const selectedNodeIdsRef = useRef(new Set<string>());
  const selectionAnchorIdRef = useRef("");
  const multiSelectionKeyboardActiveRef = useRef(false);
  const indentSelectionRef = useRef<((current: FlatNodeData) => Promise<void>) | null>(null);
  const deleteSelectionRef = useRef<((current: FlatNodeData) => Promise<void>) | null>(null);
  const editingNodeIdRef = useRef("");
  const nodeSelectionDragRef = useRef<NodeSelectionDrag | null>(null);
  const suppressSelectionClickRef = useRef(false);
  const inputRefs = useRef(new Map<string, HTMLTextAreaElement>());
  const resolvedTempNodeIdsRef = useRef(new Map<string, string>());
  const outlineSurfaceRef = useRef<HTMLDivElement | null>(null);
  const virtualListRef = useRef<HTMLDivElement | null>(null);
  const [compactLayout, setCompactLayout] = useState(() => window.matchMedia("(max-width: 760px)").matches);
  const [listScrollMargin, setListScrollMargin] = useState(0);
  const contentGridRef = useRef<HTMLElement | null>(null);
  const panelResizeCleanupRef = useRef<(() => void) | null>(null);
  const flatStateRef = useRef<FlatTreeState | null>(null);
  const selectedIndexRef = useRef(-1);
  const cancelledTempIdsRef = useRef(new Set<string>());
  const localNodeTitlesRef = useRef(new Map<string, string>());
  const reconcilingNodeIdsRef = useRef(new Set<string>());
  const pendingNodeCreateCountRef = useRef(0);
  const nodeCreateWaitersRef = useRef(new Set<() => void>());
  const nodeCreateQueueRef = useRef<Promise<void>>(Promise.resolve());
  const nodePatchQueuesRef = useRef(new Map<string, Promise<void>>());
  const nodeCompositionTrackerRef = useRef<NodeCompositionTracker | null>(null);
  const pendingNodeRevealRef = useRef<{ workspaceId: string; nodeId: string } | null>(null);
  if (!nodeCompositionTrackerRef.current) {
    nodeCompositionTrackerRef.current = new NodeCompositionTracker(callback => {
      window.requestAnimationFrame(callback);
    });
  }
  const nodeCompositionTracker = nodeCompositionTrackerRef.current;

  useEffect(() => {
    storeBoolean(SIDEBAR_COLLAPSED_STORAGE_KEY, sidebarCollapsed);
  }, [sidebarCollapsed]);

  useEffect(() => {
    if (!mobileSidebarOpen) return;
    const closeOnEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") setMobileSidebarOpen(false);
    };
    const media = window.matchMedia("(max-width: 980px)");
    const closeOnDesktop = () => {
      if (!media.matches) setMobileSidebarOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    media.addEventListener("change", closeOnDesktop);
    return () => {
      window.removeEventListener("keydown", closeOnEscape);
      media.removeEventListener("change", closeOnDesktop);
    };
  }, [mobileSidebarOpen]);

  useEffect(() => {
    storeIdSet(COLLAPSED_WORKSPACE_FOLDERS_STORAGE_KEY, collapsedWorkspaceFolderIds);
  }, [collapsedWorkspaceFolderIds]);

  useEffect(() => {
    storeIdSet(COLLAPSED_WORKSPACES_STORAGE_KEY, collapsedWorkspaceIds);
  }, [collapsedWorkspaceIds]);

  useEffect(() => {
    storeIdSet(COLLAPSED_SYSTEM_TAGS_STORAGE_KEY, collapsedSystemTags);
  }, [collapsedSystemTags]);

  useEffect(() => {
    storePanelWidth(SIDEBAR_WIDTH_STORAGE_KEY, sidebarWidth);
  }, [sidebarWidth]);

  useEffect(() => {
    storePanelWidth(INSPECTOR_WIDTH_STORAGE_KEY, inspectorWidth);
  }, [inspectorWidth]);

  useEffect(() => () => panelResizeCleanupRef.current?.(), []);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 760px)");
    const update = () => setCompactLayout(media.matches);
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  const panelWidthBounds = (panel: ResizablePanel) => {
    if (panel === "sidebar") {
      const inspectorSpace = isInspectorOpen ? inspectorWidth : 0;
      const responsiveMax = window.innerWidth - inspectorSpace - 360;
      return {
        min: MIN_SIDEBAR_WIDTH,
        max: Math.max(MIN_SIDEBAR_WIDTH, Math.min(MAX_SIDEBAR_WIDTH, responsiveMax))
      };
    }

    const contentWidth = contentGridRef.current?.getBoundingClientRect().width ?? window.innerWidth - sidebarWidth;
    return {
      min: MIN_INSPECTOR_WIDTH,
      max: Math.max(MIN_INSPECTOR_WIDTH, Math.min(MAX_INSPECTOR_WIDTH, contentWidth - 360))
    };
  };

  const updatePanelWidth = (panel: ResizablePanel, width: number) => {
    const bounds = panelWidthBounds(panel);
    const nextWidth = clampPanelWidth(width, bounds.min, bounds.max);
    if (panel === "sidebar") setSidebarWidth(nextWidth);
    else setInspectorWidth(nextWidth);
  };

  const startPanelResize = (panel: ResizablePanel, event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    panelResizeCleanupRef.current?.();

    const startX = event.clientX;
    const startWidth = panel === "sidebar" ? sidebarWidth : inspectorWidth;
    const move = (pointerEvent: globalThis.PointerEvent) => {
      const delta = pointerEvent.clientX - startX;
      updatePanelWidth(panel, startWidth + (panel === "sidebar" ? delta : -delta));
    };
    const finish = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", finish);
      window.removeEventListener("pointercancel", finish);
      document.body.classList.remove("isResizingPanel");
      panelResizeCleanupRef.current = null;
    };

    panelResizeCleanupRef.current = finish;
    document.body.classList.add("isResizingPanel");
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", finish);
    window.addEventListener("pointercancel", finish);
  };

  const resizePanelWithKeyboard = (panel: ResizablePanel, event: KeyboardEvent<HTMLDivElement>) => {
    const direction = event.key === "ArrowLeft" ? -1 : event.key === "ArrowRight" ? 1 : 0;
    if (!direction) return;
    event.preventDefault();
    const currentWidth = panel === "sidebar" ? sidebarWidth : inspectorWidth;
    const panelDirection = panel === "sidebar" ? direction : -direction;
    updatePanelWidth(panel, currentWidth + panelDirection * PANEL_RESIZE_STEP);
  };

  const setNodeSelection = useCallback((ids: Iterable<string>, primaryId: string, anchorId = primaryId) => {
    const next = new Set(ids);
    if (primaryId) next.add(primaryId);
    selectedNodeIdsRef.current = next;
    selectedIdRef.current = primaryId;
    selectionAnchorIdRef.current = anchorId;
    multiSelectionKeyboardActiveRef.current = next.size > 1;
    setSelectedNodeIds(next);
    setSelectedId(primaryId);
  }, []);

  const setSingleSelectedId = useCallback((id: string) => {
    setNodeSelection(id ? [id] : [], id);
  }, [setNodeSelection]);

  const loadWorkspaces = useCallback(async () => {
    const next = await apiGet<Workspace[]>("/api/workspaces");
    const currentId = workspaceIdRef.current;
    const nextId = currentId === SYSTEM_TAGS_WORKSPACE_ID
      || currentId === SYSTEM_RECYCLE_BIN_WORKSPACE_ID
      || next.some(workspace => workspace.id === currentId)
      ? currentId
      : next[0]?.id || "";
    workspaceIdRef.current = nextId;
    setWorkspaces(next);
    setWorkspaceId(nextId);
    return next;
  }, []);

  const loadWorkspaceFolders = useCallback(async () => {
    const next = await apiGet<WorkspaceFolder[]>("/api/workspace-folders");
    setWorkspaceFolders(next);
    return next;
  }, []);

  const loadTree = useCallback(async (id: string, options: LoadTreeOptions = {}) => {
    const requestId = ++treeRequestRef.current;
    if (!id || id === SYSTEM_TAGS_WORKSPACE_ID || id === SYSTEM_RECYCLE_BIN_WORKSPACE_ID) {
      setFlatState(null);
      setVisibleIds([]);
      setSingleSelectedId("");
      return;
    }
    let next: OutlineTreeNode;
    try {
      next = await apiGet<OutlineTreeNode>(`/api/workspaces/${id}/tree`);
    } catch (error) {
      if (requestId !== treeRequestRef.current || id !== workspaceIdRef.current) return;
      throw error;
    }
    if (requestId !== treeRequestRef.current || id !== workspaceIdRef.current) return;
    const loaded = fromNestedTree(next);
    const pendingReveal = pendingNodeRevealRef.current;
    const revealed = pendingReveal?.workspaceId === id
      ? revealNodeInFlatTree(loaded.state, pendingReveal.nodeId)
      : { state: loaded.state, visibleIds: loaded.visibleIds, index: -1 };
    const state = revealed.state;
    setFlatState(state);
    setVisibleIds(revealed.visibleIds);
    flatStateRef.current = state;
    const pendingFocusId = pendingWorkspaceFocusIdRef.current;
    const pendingRevealId = pendingReveal?.workspaceId === id && hasNode(state, pendingReveal.nodeId)
      ? pendingReveal.nodeId
      : "";
    const current = selectedIdRef.current;
    const nextSelectedId = pendingRevealId
      || (pendingFocusId && hasNode(state, pendingFocusId)
      ? pendingFocusId
      : options.preserveSelection && current && hasNode(state, current)
        ? current
        : state.rootId);
    if (pendingFocusId === nextSelectedId) pendingWorkspaceFocusIdRef.current = "";
    const preservedIds = options.preserveSelection && !pendingRevealId && !pendingFocusId
      ? [...selectedNodeIdsRef.current].filter(nodeId => hasNode(state, nodeId))
      : [];
    if (preservedIds.length > 1) {
      const primaryId = preservedIds.includes(nextSelectedId) ? nextSelectedId : preservedIds[0];
      const anchorId = preservedIds.includes(selectionAnchorIdRef.current) ? selectionAnchorIdRef.current : primaryId;
      setNodeSelection(preservedIds, primaryId, anchorId);
    } else {
      setSingleSelectedId(nextSelectedId);
    }
  }, [setNodeSelection, setSingleSelectedId]);

  const loadOutlineHistory = useCallback(async (id: string) => {
    const requestId = ++outlineHistoryRequestRef.current;
    if (!id || id === SYSTEM_TAGS_WORKSPACE_ID || id === SYSTEM_RECYCLE_BIN_WORKSPACE_ID) {
      setOutlineHistory(EMPTY_OUTLINE_HISTORY);
      return;
    }
    const next = await apiGet<OutlineHistoryState>(`/api/workspaces/${id}/history`);
    if (requestId !== outlineHistoryRequestRef.current || id !== workspaceIdRef.current) return;
    setOutlineHistory(next);
    if (next.undoLabel !== "Delete outline") setPendingDelete(null);
  }, []);

  useEffect(() => {
    const saved = (event: Event) => {
      const { nodeId, field, value } = (event as CustomEvent<{ nodeId: string; field: "title" | "body"; value: string }>).detail;
      if (value === undefined) return;
      const current = flatStateRef.current;
      if (!current || !hasNode(current, nodeId)) return;
      const next = updateNode(current, nodeId, { [field]: readNodeDraft(nodeId, field) ?? value });
      flatStateRef.current = next;
      setFlatState(next);
      void loadOutlineHistory(workspaceIdRef.current).catch(toError(setError));
    };
    window.addEventListener("outliner-draft-saved", saved);
    window.addEventListener("outliner-draft-resolved", saved);
    return () => {
      window.removeEventListener("outliner-draft-saved", saved);
      window.removeEventListener("outliner-draft-resolved", saved);
    };
  }, [loadOutlineHistory]);

  const loadTags = useCallback(async (id: string) => {
    const requestId = ++tagsRequestRef.current;
    if (!id) {
      setTags([]);
      return;
    }
    let next: Tag[];
    try {
      next = await apiGet<Tag[]>("/api/system/tags");
    } catch (error) {
      if (requestId !== tagsRequestRef.current || id !== workspaceIdRef.current) return;
      throw error;
    }
    if (requestId !== tagsRequestRef.current || id !== workspaceIdRef.current) return;
    setTags(next);
  }, []);

  const loadTagResults = useCallback(async (name: string) => {
    const requestId = ++tagResultsRequestRef.current;
    const normalized = name.trim().replace(/^#/, "");
    if (!normalized) {
      setActiveTagFilter("");
      setTagResults([]);
      return;
    }
    setActiveTagFilter(normalized);
    const next = await apiGet<TaggedNodeResult[]>(`/api/tag-results?name=${encodeURIComponent(normalized)}`);
    if (requestId !== tagResultsRequestRef.current) return;
    setTagResults(next);
  }, []);

  const loadSystemTagGroups = useCallback(async () => {
    const requestId = ++systemTagGroupsRequestRef.current;
    let next: TaggedNodeGroup[];
    try {
      next = await apiGet<TaggedNodeGroup[]>("/api/system/tag-tree?includeUnused=1");
    } catch (error) {
      if (requestId !== systemTagGroupsRequestRef.current || workspaceIdRef.current !== SYSTEM_TAGS_WORKSPACE_ID) {
        return;
      }
      throw error;
    }
    if (requestId !== systemTagGroupsRequestRef.current || workspaceIdRef.current !== SYSTEM_TAGS_WORKSPACE_ID) return;
    setSystemTagGroups(next);
  }, []);

  const loadRecycleBin = useCallback(async () => {
    const requestId = ++recycleBinRequestRef.current;
    const next = await apiGet<RecycleBinEntry[]>("/api/recycle-bin");
    if (requestId !== recycleBinRequestRef.current || workspaceIdRef.current !== SYSTEM_RECYCLE_BIN_WORKSPACE_ID) return;
    setRecycleBinEntries(next);
  }, []);

  useEffect(() => {
    loadWorkspaces().catch(toError(setError));
  }, [loadWorkspaces]);

  useEffect(() => {
    let pending = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const refresh = () => {
      if (!pending) return;
      const active = document.activeElement;
      if (active instanceof HTMLElement && (active.isContentEditable || active.matches("input, textarea"))) return;
      pending = false;
      void loadWorkspaces().then(() => Promise.all([
        loadWorkspaceFolders(), loadTree(workspaceIdRef.current, { preserveSelection: true }),
        loadTags(workspaceIdRef.current), loadOutlineHistory(workspaceIdRef.current),
        workspaceIdRef.current === SYSTEM_RECYCLE_BIN_WORKSPACE_ID ? loadRecycleBin() : Promise.resolve()
      ])).catch(toError(setError));
    };
    const onSync = () => { pending = true; refresh(); };
    const onBlur = () => { clearTimeout(timer); timer = setTimeout(refresh, 100); };
    window.addEventListener("outliner-sync", onSync);
    window.addEventListener("focusout", onBlur);
    return () => { clearTimeout(timer); window.removeEventListener("outliner-sync", onSync); window.removeEventListener("focusout", onBlur); };
  }, [loadWorkspaces, loadWorkspaceFolders, loadTree, loadTags, loadOutlineHistory, loadRecycleBin]);


  useEffect(() => {
    loadWorkspaceFolders().catch(toError(setError));
  }, [loadWorkspaceFolders]);

  useEffect(() => {
    loadTree(workspaceId).catch(toError(setError));
  }, [loadTree, workspaceId]);

  useEffect(() => {
    loadTags(workspaceId).catch(toError(setError));
  }, [loadTags, workspaceId]);

  useEffect(() => {
    loadOutlineHistory(workspaceId).catch(toError(setError));
  }, [loadOutlineHistory, workspaceId]);

  useEffect(() => {
    if (workspaceId !== SYSTEM_TAGS_WORKSPACE_ID) return;
    const refresh = () => {
      if (document.visibilityState === "visible") loadSystemTagGroups().catch(toError(setError));
    };
    refresh();
    const interval = window.setInterval(refresh, 5000);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [loadSystemTagGroups, workspaceId]);

  useEffect(() => {
    if (workspaceId !== SYSTEM_RECYCLE_BIN_WORKSPACE_ID) return;
    const refresh = () => {
      if (document.visibilityState === "visible") loadRecycleBin().catch(toError(setError));
    };
    refresh();
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [loadRecycleBin, workspaceId]);

  useEffect(() => {
    setPendingDelete(null);
  }, [workspaceId]);

  useEffect(() => {
    if (!convertWorkspaceCandidate || isConvertingWorkspace) return;
    const closeOnEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") setConvertWorkspaceCandidate(null);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [convertWorkspaceCandidate, isConvertingWorkspace]);

  useEffect(() => {
    if (!pendingDelete) return;
    const timer = window.setTimeout(() => setPendingDelete(current =>
      current?.createdAt === pendingDelete.createdAt ? null : current
    ), 6000);
    return () => window.clearTimeout(timer);
  }, [pendingDelete]);

  useEffect(() => {
    flatStateRef.current = flatState;
  }, [flatState]);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 760px)");
    const closeInspectorForMobile = () => {
      if (media.matches) setIsInspectorOpen(false);
    };
    closeInspectorForMobile();
    media.addEventListener("change", closeInspectorForMobile);
    return () => media.removeEventListener("change", closeInspectorForMobile);
  }, []);

  const selectedNode = selectedId && flatState ? getNode(flatState, selectedId) : undefined;
  const selectedWorkspace = workspaces.find(workspace => workspace.id === workspaceId);
  const isSystemTagsWorkspace = workspaceId === SYSTEM_TAGS_WORKSPACE_ID;
  const isRecycleBinWorkspace = workspaceId === SYSTEM_RECYCLE_BIN_WORKSPACE_ID;
  const [dueReminderCutoff, setDueReminderCutoff] = useState(() => getDueReminderCutoff());
  const [allDueReminderNodes, setAllDueReminderNodes] = useState<OutlineNode[]>([]);
  useEffect(() => {
    let requestId = 0;
    const refresh = async () => {
      const currentRequest = ++requestId;
      try {
        const nodes = await apiGet<OutlineNode[]>(`/api/due-reminders?cutoff=${dueReminderCutoff}`);
        if (currentRequest === requestId) setAllDueReminderNodes(nodes);
      } catch (error) {
        if (currentRequest === requestId) toError(setError)(error);
      }
    };
    void refresh();
    window.addEventListener("outliner-data-changed", refresh);
    window.addEventListener("outliner-sync", refresh);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      requestId += 1;
      window.removeEventListener("outliner-data-changed", refresh);
      window.removeEventListener("outliner-sync", refresh);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [dueReminderCutoff]);
  useEffect(() => {
    const refresh = () => setDueReminderCutoff(getDueReminderCutoff());
    const interval = window.setInterval(refresh, 60_000);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, []);
  const overdueNodes = useMemo(
    () => getDueReminderNodes(flatState, dueReminderCutoff, allDueReminderNodes),
    [flatState, dueReminderCutoff, allDueReminderNodes]
  );
  const draggingNodeIds = useMemo(() => new Set(dragState?.draggingIds ?? []), [dragState?.draggingIds]);
  const outlineDropWorkspace = workspaces.find(workspace => workspace.id === dragState?.overWorkspaceId);
  const rootWorkspaces = useMemo(
    () => workspaces.filter(workspace => !workspace.folderId && !workspace.parentWorkspaceId),
    [workspaces]
  );
  const workspacesByFolder = useMemo(() => {
    const map = new Map<string, Workspace[]>();
    for (const folder of workspaceFolders) map.set(folder.id, []);
    for (const workspace of workspaces) {
      if (!workspace.folderId || workspace.parentWorkspaceId) continue;
      const folderWorkspaces = map.get(workspace.folderId);
      if (folderWorkspaces) folderWorkspaces.push(workspace);
    }
    return map;
  }, [workspaceFolders, workspaces]);
  const workspacesByParent = useMemo(() => {
    const map = new Map<string, Workspace[]>();
    for (const workspace of workspaces) {
      if (!workspace.parentWorkspaceId) continue;
      const children = map.get(workspace.parentWorkspaceId) ?? [];
      children.push(workspace);
      map.set(workspace.parentWorkspaceId, children);
    }
    return map;
  }, [workspaces]);
  const isSearching = search.trim().length > 0;
  const isTagFiltering = activeTagFilter.length > 0;
  const filteredNodes = useMemo(() => isSearching && flatState
    ? searchNodeIds(flatState, search)
    : visibleIds, [isSearching, flatState, search, visibleIds]);
  const filteredTagResults = isSearching
    ? tagResults.filter(result =>
        `${result.node.title}\n${result.node.body}\n${result.workspace.name}`.toLowerCase().includes(search.toLowerCase())
      )
    : tagResults;
  const systemTagRows = useMemo(
    () => buildSystemTagRows(systemTagGroups, collapsedSystemTags, search),
    [collapsedSystemTags, search, systemTagGroups]
  );
  const filteredRecycleBinEntries = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return recycleBinEntries;
    return recycleBinEntries.filter(entry =>
      entry.workspace.name.toLowerCase().includes(query)
      || entry.node.title.toLowerCase().includes(query)
      || entry.node.body.toLowerCase().includes(query)
      || entry.descendants.some(node => `${node.title}\n${node.body}`.toLowerCase().includes(query))
    );
  }, [recycleBinEntries, search]);
  const visibleItemCount = isRecycleBinWorkspace
    ? filteredRecycleBinEntries.length
    : isSystemTagsWorkspace
      ? systemTagRows.length
    : isTagFiltering
      ? filteredTagResults.length
      : filteredNodes.length;
  const selectedIndex = selectedId
    ? isSystemTagsWorkspace
      ? -1
      : isTagFiltering
      ? filteredTagResults.findIndex(result => result.node.id === selectedId)
      : filteredNodes.findIndex(id => id === selectedId)
    : -1;
  selectedIndexRef.current = selectedIndex;
  const getOutlineScrollElement = () => compactLayout ? contentGridRef.current : outlineSurfaceRef.current;
  const rowVirtualizer = useVirtualizer({
    count: visibleItemCount,
    getScrollElement: getOutlineScrollElement,
    scrollMargin: listScrollMargin,
    getItemKey: index =>
      isRecycleBinWorkspace
        ? filteredRecycleBinEntries[index]?.node.id ?? `recycle-bin-${index}`
        : isSystemTagsWorkspace
        ? systemTagRowKey(systemTagRows[index], index)
        : isTagFiltering
        ? filteredTagResults[index]?.node.id ?? `tag-result-${index}`
        : filteredNodes[index] ?? index,
    measureElement: element => Math.ceil(element.getBoundingClientRect().height),
    estimateSize: index => isRecycleBinWorkspace
      ? 92
      : isSystemTagsWorkspace && systemTagRows[index]?.kind === "node" ? 46 : 38,
    overscan: 16,
    useAnimationFrameWithResizeObserver: true
  });
  const virtualItems = rowVirtualizer.getVirtualItems();

  useLayoutEffect(() => {
    const surface = getOutlineScrollElement();
    const list = virtualListRef.current;
    if (!surface || !list) return;
    const measure = () => setListScrollMargin(list.getBoundingClientRect().top - surface.getBoundingClientRect().top + surface.scrollTop);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(surface);
    const header = outlineSurfaceRef.current?.querySelector(".outlineHeader");
    if (header) observer.observe(header);
    return () => observer.disconnect();
  }, [compactLayout, workspaceId, visibleItemCount > 0]);

  useEffect(() => {
    rowVirtualizer.shouldAdjustScrollPositionOnItemSizeChange = (item, delta, instance) => {
      if (item.index === selectedIndexRef.current) return false;
      if (Math.abs(delta) < 1) return false;
      return item.end <= (instance.scrollOffset ?? 0);
    };

    return () => {
      rowVirtualizer.shouldAdjustScrollPositionOnItemSizeChange = undefined;
    };
  }, [rowVirtualizer]);

  const focusWhenReady = useCallback((nodeId: string, selection?: TitleSelection, attempts = 0) => {
    const input = inputRefs.current.get(nodeId);
    if (input) {
      focusTitleInput(input, selection);
      return;
    }
    if (attempts < 10) {
      window.requestAnimationFrame(() => focusWhenReady(nodeId, selection, attempts + 1));
    }
  }, []);

  const focusLocatedNodeWhenReady = useCallback((nodeId: string, attempts = 0) => {
    const input = inputRefs.current.get(nodeId);
    if (input) {
      focusTitleInput(input);
      input.scrollIntoView({ block: "center", inline: "nearest" });
      return;
    }
    if (attempts < 20) {
      window.requestAnimationFrame(() => focusLocatedNodeWhenReady(nodeId, attempts + 1));
    }
  }, []);

  useLayoutEffect(() => {
    const pending = pendingNodeRevealRef.current;
    if (!pending || pending.workspaceId !== workspaceId || pending.nodeId !== selectedId) return;
    const index = filteredNodes.indexOf(pending.nodeId);
    if (index < 0) return;

    pendingNodeRevealRef.current = null;
    rowVirtualizer.scrollToIndex(index, { align: "center" });
    window.requestAnimationFrame(() => focusLocatedNodeWhenReady(pending.nodeId));
  }, [filteredNodes, focusLocatedNodeWhenReady, rowVirtualizer, selectedId, workspaceId]);

  const runOutlineHistory = useCallback(async (
    direction: "undo" | "redo",
    preferredFocusId?: string,
    preferredSelection?: { ids: string[]; primaryId: string; anchorId: string }
  ) => {
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    await Promise.resolve();
    await nodeCreateQueueRef.current.catch(() => undefined);
    await Promise.all([...nodePatchQueuesRef.current.values()].map(request => request.catch(() => undefined)));

    const currentWorkspaceId = workspaceIdRef.current;
    if (!currentWorkspaceId) return;
    outlineHistoryRequestRef.current += 1;
    const result = await apiPost<OutlineHistoryResult>(
      `/api/workspaces/${currentWorkspaceId}/${direction}`,
      {}
    );
    if (currentWorkspaceId !== workspaceIdRef.current) return;

    const { state, visibleIds: nextVisibleIds } = fromNestedTree(result.tree);
    const currentSelectedId = selectedIdRef.current;
    const restoredSelectionIds = preferredSelection?.ids.filter(id => hasNode(state, id)) ?? [];
    const restoredSelection = new Set(restoredSelectionIds);
    const restoredPrimaryId = preferredSelection?.primaryId && restoredSelection.has(preferredSelection.primaryId)
      ? preferredSelection.primaryId
      : restoredSelectionIds[0];
    const nextSelectedId = restoredPrimaryId
      ? restoredPrimaryId
      : preferredFocusId && hasNode(state, preferredFocusId)
      ? preferredFocusId
      : currentSelectedId && hasNode(state, currentSelectedId)
        ? currentSelectedId
        : nextVisibleIds[0] ?? state.rootId;
    localNodeTitlesRef.current.clear();
    reconcilingNodeIdsRef.current.clear();
    setFlatState(state);
    setVisibleIds(nextVisibleIds);
    flatStateRef.current = state;
    setOutlineHistory(result.history);
    setPendingDelete(null);
    if (restoredPrimaryId) {
      const restoredAnchorId = preferredSelection?.anchorId && restoredSelection.has(preferredSelection.anchorId)
        ? preferredSelection.anchorId
        : restoredPrimaryId;
      setNodeSelection(restoredSelectionIds, restoredPrimaryId, restoredAnchorId);
    } else {
      setSingleSelectedId(nextSelectedId);
    }
    if (nextSelectedId !== state.rootId && restoredSelectionIds.length <= 1) {
      window.setTimeout(() => focusWhenReady(nextSelectedId), 30);
    }
  }, [focusWhenReady, setNodeSelection, setSingleSelectedId]);

  useEffect(() => {
    const handleOutlineHistoryShortcut = (event: globalThis.KeyboardEvent) => {
      const direction = getOutlineHistoryShortcut(
        event,
        outlineHistory.canUndo,
        outlineHistory.canRedo,
        isEditableElement(event.target),
        isOutlineEditableElement(event.target)
      );
      if (!direction) return;
      event.preventDefault();
      runOutlineHistory(direction).catch(toError(setError));
    };

    window.addEventListener("keydown", handleOutlineHistoryShortcut);
    return () => window.removeEventListener("keydown", handleOutlineHistoryShortcut);
  }, [outlineHistory.canRedo, outlineHistory.canUndo, runOutlineHistory]);

  const patchNode = (id: string, patch: Partial<OutlineTreeNode>): Promise<void> => {
    if (id.startsWith("temp-")) return Promise.resolve();
    const previous = nodePatchQueuesRef.current.get(id) ?? Promise.resolve();
    const request = previous
      .catch(() => undefined)
      .then(() => apiPatch(`/api/nodes/${id}`, patch))
      .then(() => {
        loadOutlineHistory(workspaceIdRef.current).catch(toError(setError));
      });
    nodePatchQueuesRef.current.set(id, request);
    return request.finally(() => {
      if (nodePatchQueuesRef.current.get(id) === request) nodePatchQueuesRef.current.delete(id);
    });
  };

  const focusNode = (id: string) => {
    setSingleSelectedId(id);
    window.setTimeout(() => focusTitleInput(inputRefs.current.get(id)), 30);
  };

  const selectNode = (id: string) => {
    setSingleSelectedId(id);
  };

  const preserveOutlineScroll = () => {
    const element = getOutlineScrollElement();
    const scrollTop = element?.scrollTop;
    if (!element || scrollTop === undefined) return () => {};

    const restore = () => {
      element.scrollTop = scrollTop;
    };
    window.requestAnimationFrame(restore);
    window.setTimeout(restore, 0);
    window.setTimeout(restore, 50);
    return restore;
  };

  const clearTagFilter = () => {
    tagResultsRequestRef.current += 1;
    setActiveTagFilter("");
    setTagResults([]);
  };

  const openTagResult = async (result: TaggedNodeResult) => {
    clearTagFilter();
    setSearch("");
    systemTagGroupsRequestRef.current += 1;
    const isCurrentWorkspace = workspaceIdRef.current === result.workspace.id;
    pendingNodeRevealRef.current = { workspaceId: result.workspace.id, nodeId: result.node.id };
    workspaceIdRef.current = result.workspace.id;
    treeRequestRef.current += 1;
    tagsRequestRef.current += 1;
    setWorkspaceId(result.workspace.id);
    setFlatState(null);
    setSingleSelectedId("");
    setTags([]);
    if (!isCurrentWorkspace) return;
    try {
      await Promise.all([loadTree(result.workspace.id), loadTags(result.workspace.id)]);
    } catch (error) {
      pendingNodeRevealRef.current = null;
      throw error;
    }
  };

  const createOptimisticNode = async (
    parentId: string,
    position: number,
    current?: FlatNodeData,
    title = "",
    currentTitle = current?.title
  ) => {
    // Use flatStateRef to avoid stale closure when called together with onPatchLocal
    const currentFlatState = flatStateRef.current;
    if (!currentFlatState) return;
    const tempId = `temp-${crypto.randomUUID()}`;
    const preppedState =
      current && currentTitle !== undefined ? updateNode(currentFlatState, current.id, { title: currentTitle }) : currentFlatState;
    const tempNode: FlatNodeData = {
      id: tempId,
      workspaceId: currentFlatState.nodes[currentFlatState.rootId].workspaceId,
      parentId,
      position,
      title,
      body: "",
      dueDate: null,
      done: false,
      collapsed: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      tags: [],
      fieldValues: [],
      childIds: []
    };

    const currentTitleSave = current && !current.id.startsWith("temp-") ? flushNodeDraft(current.id, "title") : Promise.resolve();
    const newState = insertNode(preppedState, parentId, tempNode, position);
    setFlatState(newState);
    setVisibleIds(computeVisibleIds(newState));
    flatStateRef.current = newState;
    setSingleSelectedId(tempId);
    editingNodeIdRef.current = tempId;
    focusWhenReady(tempId);
    pendingNodeCreateCountRef.current += 1;

    try {
      const createRequest = nodeCreateQueueRef.current.then(() => currentTitleSave).then(() =>
        apiPost<OutlineTreeNode>("/api/nodes", {
          parentId,
          title,
          position
        })
      );
      nodeCreateQueueRef.current = createRequest.then(() => undefined, () => undefined);
      const created = await createRequest;
      resolvedTempNodeIdsRef.current.set(tempId, created.id);
      loadOutlineHistory(workspaceIdRef.current).catch(toError(setError));
      if (cancelledTempIdsRef.current.has(tempId)) {
        cancelledTempIdsRef.current.delete(tempId);
        localNodeTitlesRef.current.delete(tempId);
        apiDelete(`/api/nodes/${created.id}`)
          .then(() => loadOutlineHistory(workspaceIdRef.current))
          .catch(toError(setError));
        return;
      }
      await nodeCompositionTracker.wait(tempId);
      if (cancelledTempIdsRef.current.has(tempId)) {
        cancelledTempIdsRef.current.delete(tempId);
        localNodeTitlesRef.current.delete(tempId);
        apiDelete(`/api/nodes/${created.id}`)
          .then(() => loadOutlineHistory(workspaceIdRef.current))
          .catch(toError(setError));
        return;
      }
      const draft = flatStateRef.current ? getNode(flatStateRef.current, tempId) : undefined;
      const pendingTitle = localNodeTitlesRef.current.get(tempId);
      const draftParentId = draft?.parentId ?? parentId;
      const createdPosition = created.position ?? position;
      const draftPosition = draft?.position ?? createdPosition;
      const latestTitle = resolvePendingNodeTitle(pendingTitle, draft?.title, created.title);
      const replacement: FlatNodeData = {
        id: created.id,
        workspaceId: created.workspaceId,
        parentId: draftParentId,
        position: draftPosition,
        title: latestTitle,
        body: draft?.body ?? created.body ?? "",
        dueDate: draft?.dueDate ?? created.dueDate ?? null,
        done: draft?.done ?? created.done ?? false,
        collapsed: draft?.collapsed ?? created.collapsed ?? false,
        createdAt: created.createdAt ?? new Date().toISOString(),
        updatedAt: created.updatedAt ?? new Date().toISOString(),
        tags: draft?.tags ?? created.tags ?? [],
        fieldValues: draft?.fieldValues ?? created.fieldValues ?? [],
        childIds: draft?.childIds ?? [],
      };
      const currentRef = applyCachedNodeTitles(flatStateRef.current ?? newState, localNodeTitlesRef.current);
      const withCreated = draft
        ? replaceNode(currentRef, tempId, replacement)
        : insertNode(removeNode(currentRef, tempId), parentId, replacement, position);
      const tempWasSelected = selectedNodeIdsRef.current.has(tempId);
      const tempWasPrimary = selectedIdRef.current === tempId;
      const tempWasAnchor = selectionAnchorIdRef.current === tempId;
      const tempWasEditing = editingNodeIdRef.current === tempId;
      const tempInput = inputRefs.current.get(tempId);
      const tempSelection = tempWasEditing && tempInput
        ? {
            start: tempInput.selectionStart ?? tempInput.value.length,
            end: tempInput.selectionEnd ?? tempInput.selectionStart ?? tempInput.value.length
          }
        : undefined;
      if (tempWasEditing) editingNodeIdRef.current = created.id;
      setFlatState(withCreated);
      setVisibleIds(computeVisibleIds(withCreated));
      flatStateRef.current = withCreated;
      localNodeTitlesRef.current.delete(tempId);
      localNodeTitlesRef.current.set(created.id, latestTitle);
      reconcilingNodeIdsRef.current.add(created.id);
      if (tempWasSelected || tempWasPrimary || tempWasAnchor || tempWasEditing) {
        const nextSelectedIds = new Set(selectedNodeIdsRef.current);
        nextSelectedIds.delete(tempId);
        nextSelectedIds.add(created.id);
        setNodeSelection(
          nextSelectedIds,
          tempWasPrimary || tempWasEditing ? created.id : selectedIdRef.current,
          tempWasAnchor ? created.id : selectionAnchorIdRef.current
        );
        if (tempWasPrimary || tempWasEditing) focusWhenReady(created.id, tempSelection);
      }
      if (
        draft &&
        draftParentId &&
        !draftParentId.startsWith("temp-") &&
        (draftParentId !== parentId || draftPosition !== createdPosition)
      ) {
        apiPost(`/api/nodes/${created.id}/move`, {
          parentId: draftParentId,
          position: draftPosition
        })
          .then(() => loadOutlineHistory(workspaceIdRef.current))
          .catch(toError(setError));
      }
      if (pendingTitle !== undefined || (draft && (draft.title || draft.body || draft.dueDate || draft.done || draft.collapsed))) {
        patchNode(created.id, {
          title: latestTitle,
          body: replacement.body,
          dueDate: replacement.dueDate,
          done: replacement.done,
          collapsed: replacement.collapsed
        }).catch(toError(setError));
      }
    } catch (error) {
      if (cancelledTempIdsRef.current.has(tempId)) {
        cancelledTempIdsRef.current.delete(tempId);
        localNodeTitlesRef.current.delete(tempId);
        return;
      }
      const latestState = flatStateRef.current;
      const withoutFailed = latestState && getNode(latestState, tempId)
        ? removeNode(latestState, tempId)
        : latestState;
      localNodeTitlesRef.current.delete(tempId);
      if (withoutFailed) {
        setFlatState(withoutFailed);
        setVisibleIds(computeVisibleIds(withoutFailed));
        flatStateRef.current = withoutFailed;
      }
      if (selectedIdRef.current === tempId || editingNodeIdRef.current === tempId) {
        editingNodeIdRef.current = current?.id ?? "";
        focusNode(current?.id ?? parentId);
      }
      throw error;
    } finally {
      pendingNodeCreateCountRef.current = Math.max(0, pendingNodeCreateCountRef.current - 1);
      if (pendingNodeCreateCountRef.current === 0) {
        for (const id of reconcilingNodeIdsRef.current) localNodeTitlesRef.current.delete(id);
        reconcilingNodeIdsRef.current.clear();
        for (const resolve of nodeCreateWaitersRef.current) resolve();
        nodeCreateWaitersRef.current.clear();
      }
    }
  };

  const deleteNodeOptimistically = async (node: FlatNodeData, action: PendingDelete["action"] = "delete") => {
    const before = flatStateRef.current;
    if (!before || node.id === before.rootId) return;
    const visibleBefore = computeVisibleIds(before);
    const selectedIds = selectedNodeIdsRef.current.has(node.id) && selectedNodeIdsRef.current.size > 1
      ? visibleBefore.filter(id => selectedNodeIdsRef.current.has(id))
      : [node.id];
    const deletingIds = getTopLevelNodeIds(before, selectedIds);
    if (deletingIds.length === 0) return;
    if (deletingIds.some(id => id.startsWith("temp-")) && deletingIds.length > 1) {
      setError("Wait for new nodes to finish saving before deleting the selection.");
      return;
    }

    const currentWorkspaceId = workspaceId;
    const selectionBefore = new Set(selectedNodeIdsRef.current);
    const primaryBefore = selectedIdRef.current;
    const anchorBefore = selectionAnchorIdRef.current;
    const deletingIdSet = new Set(deletingIds);
    const firstDeleteIndex = visibleBefore.findIndex(id => deletingIdSet.has(id));
    const newState = removeNodes(before, deletingIds);
    const removedIds = Object.keys(before.nodes).filter(id => !newState.nodes[id]);
    const previousId = visibleBefore
      .slice(0, Math.max(0, firstDeleteIndex))
      .reverse()
      .find(id => Boolean(newState.nodes[id])) ?? before.rootId;
    for (const id of removedIds) {
      localNodeTitlesRef.current.delete(id);
      reconcilingNodeIdsRef.current.delete(id);
    }
    setFlatState(newState);
    setVisibleIds(computeVisibleIds(newState));
    flatStateRef.current = newState;
    focusNode(previousId);
    if (deletingIds[0]?.startsWith("temp-")) {
      cancelledTempIdsRef.current.add(deletingIds[0]);
      return;
    }

    try {
      await apiPost("/api/nodes/delete-batch", { ids: deletingIds });
      loadOutlineHistory(currentWorkspaceId).catch(toError(setError));
      setPendingDelete({
        selectedIds,
        primaryId: selectedIds.includes(primaryBefore) ? primaryBefore : selectedIds[0],
        anchorId: selectedIds.includes(anchorBefore) ? anchorBefore : selectedIds[0],
        nodeCount: selectedIds.length,
        workspaceId: currentWorkspaceId,
        focusAfterDeleteId: previousId,
        createdAt: Date.now(),
        action
      });
    } catch (error) {
      setFlatState(before);
      setVisibleIds(computeVisibleIds(before));
      flatStateRef.current = before;
      setNodeSelection(selectionBefore, primaryBefore, anchorBefore);
      window.setTimeout(() => focusTitleInput(inputRefs.current.get(primaryBefore)), 30);
      throw error;
    }
  };
  deleteSelectionRef.current = deleteNodeOptimistically;

  useEffect(() => {
    const handleMultiSelectionDelete = (event: globalThis.KeyboardEvent) => {
      if (!shouldHandleMultiSelectionDelete(
        event,
        selectedNodeIdsRef.current.size,
        multiSelectionKeyboardActiveRef.current,
        isEditableElement(event.target),
        isOutlineEditableElement(event.target)
      )) return;

      const currentState = flatStateRef.current;
      if (!currentState) return;
      const primaryId = selectedNodeIdsRef.current.has(selectedIdRef.current)
        ? selectedIdRef.current
        : computeVisibleIds(currentState).find(id => selectedNodeIdsRef.current.has(id));
      const current = primaryId ? getNode(currentState, primaryId) : undefined;
      if (!current) return;

      event.preventDefault();
      deleteSelectionRef.current?.(current).catch(toError(setError));
    };

    window.addEventListener("keydown", handleMultiSelectionDelete);
    return () => window.removeEventListener("keydown", handleMultiSelectionDelete);
  }, []);

  const undoPendingDelete = async () => {
    const pending = pendingDelete;
    if (!pending || pending.workspaceId !== workspaceIdRef.current) return;
    try {
      await runOutlineHistory("undo", pending.primaryId, {
        ids: pending.selectedIds,
        primaryId: pending.primaryId,
        anchorId: pending.anchorId
      });
      const restoredState = flatStateRef.current;
      if (restoredState && pending.workspaceId === workspaceIdRef.current) {
        const restoredIds = pending.selectedIds.filter(id => hasNode(restoredState, id));
        const restoredIdSet = new Set(restoredIds);
        const restoredPrimaryId = restoredIdSet.has(pending.primaryId) ? pending.primaryId : restoredIds[0];
        if (restoredPrimaryId) {
          const restoredAnchorId = restoredIdSet.has(pending.anchorId) ? pending.anchorId : restoredPrimaryId;
          setNodeSelection(restoredIds, restoredPrimaryId, restoredAnchorId);
        }
      }
    } catch (error) {
      focusNode(pending.focusAfterDeleteId);
      throw error;
    }
  };

  const moveNodeOptimistically = async (
    source: FlatNodeData,
    parentId: string,
    position: number
  ) => {
    const before = flatStateRef.current;
    if (!before || source.id === parentId) return;
    const currentSource = getNode(before, source.id);
    if (!currentSource) return;
    const nextPosition =
      currentSource.parentId === parentId && currentSource.position < position ? position - 1 : position;
    if (currentSource.parentId === parentId && currentSource.position === nextPosition) return;
    const restoreScroll = preserveOutlineScroll();
    const newState = moveNode(before, currentSource.id, parentId, nextPosition);
    setFlatState(newState);
    setVisibleIds(computeVisibleIds(newState));
    flatStateRef.current = newState;
    selectNode(currentSource.id);
    restoreScroll();
    if (currentSource.id.startsWith("temp-") || parentId.startsWith("temp-")) return;

    try {
      await apiPost(`/api/nodes/${currentSource.id}/move`, { parentId, position: nextPosition });
      loadOutlineHistory(workspaceIdRef.current).catch(toError(setError));
    } catch (error) {
      setFlatState(before);
      setVisibleIds(computeVisibleIds(before));
      flatStateRef.current = before;
      focusNode(currentSource.id);
      throw error;
    } finally {
      window.setTimeout(() => {
        restoreScroll();
      }, 80);
    }
  };

  const createAfter = async (current: FlatNodeData, title = "", currentTitle = current.title) => {
    const currentFlatState = flatStateRef.current;
    if (!currentFlatState) return;
    const parentId = current.parentId ?? currentFlatState.rootId;
    await createOptimisticNode(parentId, current.position + 1, current, title, currentTitle);
  };

  const createBefore = async (current: FlatNodeData, currentTitle = current.title) => {
    const currentFlatState = flatStateRef.current;
    if (!currentFlatState) return;
    const parentId = current.parentId ?? currentFlatState.rootId;
    await createOptimisticNode(parentId, current.position, current, "", currentTitle);
  };

  const createFirstNode = async () => {
    if (!flatState) return;
    await createOptimisticNode(flatState.rootId, 0);
  };

  const indent = async (current: FlatNodeData) => {
    const currentState = flatStateRef.current;
    if (!currentState) return;
    const selectedIds = selectedNodeIdsRef.current.has(current.id)
      ? visibleIds.filter(id => selectedNodeIdsRef.current.has(id))
      : [current.id];
    const movingIds = getTopLevelNodeIds(currentState, selectedIds);
    const movingIdSet = new Set(movingIds);
    const firstMovingId = visibleIds.find(id => movingIdSet.has(id));
    if (!firstMovingId) return;
    const targetId = getIndentTargetId(currentState, visibleIds, firstMovingId);
    const target = targetId ? getNode(currentState, targetId) : undefined;
    if (!target) return;
    await moveNodesToTarget(movingIds, target, "inside");
  };
  indentSelectionRef.current = indent;

  useEffect(() => {
    const handleMultiSelectionTab = (event: globalThis.KeyboardEvent) => {
      if (!shouldHandleMultiSelectionTab(
        event,
        selectedNodeIdsRef.current.size,
        multiSelectionKeyboardActiveRef.current
      )) return;

      const currentState = flatStateRef.current;
      if (!currentState) return;
      const primaryId = selectedNodeIdsRef.current.has(selectedIdRef.current)
        ? selectedIdRef.current
        : computeVisibleIds(currentState).find(id => selectedNodeIdsRef.current.has(id));
      const current = primaryId ? getNode(currentState, primaryId) : undefined;
      if (!current) return;

      event.preventDefault();
      indentSelectionRef.current?.(current).catch(toError(setError));
    };

    window.addEventListener("keydown", handleMultiSelectionTab);
    return () => window.removeEventListener("keydown", handleMultiSelectionTab);
  }, []);

  const outdent = async (current: FlatNodeData) => {
    if (!flatState || !current.parentId || current.parentId === flatState.rootId) return;
    const parent = getNode(flatState, current.parentId);
    if (!parent?.parentId) return;
    await moveNodeOptimistically(current, parent.parentId, parent.position + 1);
  };

  const focusRelative = (current: FlatNodeData, offset: number) => {
    const index = visibleIds.indexOf(current.id);
    const nextId = visibleIds[index + offset];
    if (nextId) {
      setSingleSelectedId(nextId);
      focusTitleInput(inputRefs.current.get(nextId));
    }
  };

  const selectNodeWithMouse = (nodeId: string, event: MouseEvent<HTMLElement>) => {
    if (suppressSelectionClickRef.current) {
      suppressSelectionClickRef.current = false;
      event.preventDefault();
      return false;
    }

    const anchorId = selectionAnchorIdRef.current || selectedIdRef.current || nodeId;
    if (event.shiftKey) {
      setNodeSelection(getNodeSelectionRange(filteredNodes, anchorId, nodeId), nodeId, anchorId);
      return false;
    }

    if (event.metaKey || event.ctrlKey) {
      const next = new Set([...selectedNodeIdsRef.current].filter(id => filteredNodes.includes(id)));
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      const primaryId = next.has(nodeId)
        ? nodeId
        : next.has(selectedIdRef.current)
          ? selectedIdRef.current
          : filteredNodes.find(id => next.has(id)) ?? "";
      setNodeSelection(next, primaryId, nodeId);
      return false;
    }

    setSingleSelectedId(nodeId);
    return true;
  };

  const startNodeSelection = (node: FlatNodeData, event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || event.pointerType !== "mouse" || isTagFiltering) return;
    const target = event.target as HTMLElement;
    if (
      target.closest(".nodeTitle") ||
      target.closest(".nodeTitleLink") ||
      target.closest(".nodeMenuButton") ||
      target.closest(".disclosureButton") ||
      target.closest(".dragHandle") ||
      target.closest(".iconButton.danger") ||
      target.closest(".nodeDateControl") ||
      target.closest(".nodeTags")
    ) return;

    const anchorId = event.shiftKey
      ? selectionAnchorIdRef.current || selectedIdRef.current || node.id
      : node.id;
    const drag: NodeSelectionDrag = {
      pointerId: event.pointerId,
      anchorId,
      startX: event.clientX,
      startY: event.clientY,
      additive: event.metaKey || event.ctrlKey,
      baseSelection: event.metaKey || event.ctrlKey
        ? new Set([...selectedNodeIdsRef.current].filter(id => filteredNodes.includes(id)))
        : new Set(),
      moved: false
    };
    nodeSelectionDragRef.current = drag;
    event.currentTarget.setPointerCapture(event.pointerId);

    const move = (pointerEvent: globalThis.PointerEvent) => {
      const current = nodeSelectionDragRef.current;
      if (!current || current.pointerId !== pointerEvent.pointerId) return;
      const distance = Math.hypot(pointerEvent.clientX - current.startX, pointerEvent.clientY - current.startY);
      if (!current.moved && distance < 5) return;

      current.moved = true;
      pointerEvent.preventDefault();
      document.body.classList.add("isSelectingNodes");
      window.getSelection()?.removeAllRanges();

      const targetElement = document
        .elementFromPoint(pointerEvent.clientX, pointerEvent.clientY)
        ?.closest<HTMLElement>("[data-node-id]");
      const targetId = targetElement?.dataset.nodeId;
      if (!targetId || !filteredNodes.includes(targetId)) return;

      const range = getNodeSelectionRange(filteredNodes, current.anchorId, targetId);
      const next = current.additive ? new Set([...current.baseSelection, ...range]) : new Set(range);
      setNodeSelection(next, targetId, current.anchorId);

      const surface = getOutlineScrollElement();
      if (!surface) return;
      const bounds = surface.getBoundingClientRect();
      if (pointerEvent.clientY < bounds.top + 28) surface.scrollTop -= 18;
      else if (pointerEvent.clientY > bounds.bottom - 28) surface.scrollTop += 18;
    };

    const end = (pointerEvent: globalThis.PointerEvent) => {
      const current = nodeSelectionDragRef.current;
      if (!current || current.pointerId !== pointerEvent.pointerId) return;
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", end);
      window.removeEventListener("pointercancel", end);
      document.body.classList.remove("isSelectingNodes");
      nodeSelectionDragRef.current = null;
      if (current.moved) {
        suppressSelectionClickRef.current = true;
        window.setTimeout(() => {
          suppressSelectionClickRef.current = false;
        }, 0);
      }
    };

    window.addEventListener("pointermove", move, { passive: false });
    window.addEventListener("pointerup", end);
    window.addEventListener("pointercancel", end);
  };

  const cycleTheme = () => setTheme(nextTheme(theme));

  const startNodeDrag = (node: FlatNodeData, event: PointerEvent<HTMLButtonElement>) => {
    if (isTagFiltering || isMovingToWorkspace || event.button !== 0) return;
    const currentState = flatStateRef.current;
    if (!currentState) return;
    const draggingIds = selectedNodeIdsRef.current.has(node.id)
      ? filteredNodes.filter(id => selectedNodeIdsRef.current.has(id))
      : [node.id];
    const movingIds = getTopLevelNodeIds(currentState, draggingIds);
    if (movingIds.length === 0) return;

    event.preventDefault();
    const dragHandle = event.currentTarget;
    const pointerId = event.pointerId;
    dragHandle.setPointerCapture(pointerId);
    let openedMobileSidebar = false;
    dragTargetRef.current = null;
    if (draggingIds.length > 1) {
      setNodeSelection(draggingIds, node.id, selectionAnchorIdRef.current || node.id);
    } else {
      setSingleSelectedId(node.id);
    }
    const title = draggingIds.length > 1
      ? `${draggingIds.length} nodes`
      : readNodeDraft(node.id, "title") ?? localNodeTitlesRef.current.get(node.id) ?? node.title;
    setDragState({ draggingIds, movingIds, title, x: event.clientX, y: event.clientY });
    document.body.classList.add("isDraggingNode");

    const move = (pointerEvent: globalThis.PointerEvent) => {
      if (pointerEvent.pointerId !== pointerId) return;
      if (compactLayout && !mobileSidebarOpen && pointerEvent.clientX <= 32) {
        openedMobileSidebar = true;
        setMobileSidebarOpen(true);
      }
      const nextDragState = {
        draggingIds,
        movingIds,
        title,
        x: pointerEvent.clientX,
        y: pointerEvent.clientY
      };
      const element = document.elementFromPoint(pointerEvent.clientX, pointerEvent.clientY);
      const workspaceElement = element?.closest<HTMLElement>("[data-workspace-drop-id]");
      const targetWorkspaceId = workspaceElement?.dataset.workspaceDropId;
      if (targetWorkspaceId && targetWorkspaceId !== node.workspaceId
        && workspaces.some(workspace => workspace.id === targetWorkspaceId)) {
        dragTargetRef.current = { kind: "workspace", workspaceId: targetWorkspaceId };
        setDragState({ ...nextDragState, overWorkspaceId: targetWorkspaceId });
        return;
      }
      if (isSearching) {
        dragTargetRef.current = null;
        setDragState(nextDragState);
        return;
      }
      const targetElement = element?.closest<HTMLElement>("[data-node-id]");
      const targetId = targetElement?.dataset.nodeId;
      const target = targetId ? getNode(currentState, targetId) : undefined;

      if (
        !targetElement ||
        !target ||
        movingIds.some(id => id === target.id || isDescendant(currentState, id, target.id))
      ) {
        dragTargetRef.current = null;
        setDragState(nextDragState);
        return;
      }

      const placement = getDropPlacement(targetElement, pointerEvent.clientY);
      dragTargetRef.current = { kind: "node", overId: target.id, placement };
      setDragState({ ...nextDragState, overId: target.id, placement });
    };

    const cleanup = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", end);
      window.removeEventListener("pointercancel", cancel);
      window.removeEventListener("keydown", keydown);
      if (dragHandle.hasPointerCapture(pointerId)) dragHandle.releasePointerCapture(pointerId);
      if (openedMobileSidebar) setMobileSidebarOpen(false);
      finishNodeDrag();
    };
    const end = (pointerEvent: globalThis.PointerEvent) => {
      if (pointerEvent.pointerId !== pointerId) return;
      move(pointerEvent);
      const dropTarget = dragTargetRef.current;
      cleanup();
      if (dropTarget?.kind === "workspace") {
        moveOutlinesToWorkspace(movingIds, dropTarget.workspaceId, node.workspaceId).catch(toError(setError));
      } else if (dropTarget?.kind === "node") {
        const latestState = flatStateRef.current;
        const target = latestState ? getNode(latestState, dropTarget.overId) : undefined;
        if (target) moveNodesToTarget(movingIds, target, dropTarget.placement).catch(toError(setError));
      }
    };
    const cancel = (pointerEvent: globalThis.PointerEvent) => {
      if (pointerEvent.pointerId === pointerId) cleanup();
    };
    const keydown = (keyboardEvent: globalThis.KeyboardEvent) => {
      if (keyboardEvent.key !== "Escape") return;
      keyboardEvent.preventDefault();
      cleanup();
    };

    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", end);
    window.addEventListener("pointercancel", cancel);
    window.addEventListener("keydown", keydown);
  };

  const finishNodeDrag = () => {
    dragTargetRef.current = null;
    document.body.classList.remove("isDraggingNode");
    setDragState(null);
  };

  const moveNodesToTarget = async (ids: string[], target: FlatNodeData, placement: DropPlacement) => {
    const before = flatStateRef.current;
    if (!before) return;
    const movingIds = getTopLevelNodeIds(before, ids);
    const currentTarget = getNode(before, target.id);
    if (
      movingIds.length === 0 ||
      !currentTarget ||
      movingIds.some(id => id === currentTarget.id || isDescendant(before, id, currentTarget.id))
    ) return;

    const containsTempNode = movingIds.some(id => id.startsWith("temp-"));
    let parentId: string;
    let position: number;
    let workingState = before;
    const movingIdSet = new Set(movingIds);
    if (placement === "inside") {
      parentId = currentTarget.id;
      position = currentTarget.childIds.filter(id => !movingIdSet.has(id)).length;
      if (currentTarget.collapsed) workingState = updateNode(workingState, currentTarget.id, { collapsed: false });
    } else {
      parentId = currentTarget.parentId ?? before.rootId;
      const remainingSiblings = before.nodes[parentId].childIds.filter(id => !movingIdSet.has(id));
      const targetIndex = remainingSiblings.indexOf(currentTarget.id);
      if (targetIndex < 0) return;
      position = targetIndex + (placement === "after" ? 1 : 0);
    }
    if (movingIds.length > 1 && (containsTempNode || parentId.startsWith("temp-"))) {
      throw new Error("Wait for new nodes to finish saving before moving the selection.");
    }

    const restoreScroll = preserveOutlineScroll();
    const newState = moveNodes(workingState, movingIds, parentId, position);
    if (newState === workingState) return;
    const selectionBefore = new Set(selectedNodeIdsRef.current);
    const primaryBefore = selectedIdRef.current;
    const anchorBefore = selectionAnchorIdRef.current;
    setFlatState(newState);
    setVisibleIds(computeVisibleIds(newState));
    flatStateRef.current = newState;
    setNodeSelection(selectionBefore, primaryBefore, anchorBefore);
    restoreScroll();

    if (containsTempNode || parentId.startsWith("temp-")) return;
    try {
      await apiPost("/api/nodes/move-batch", {
        ids: movingIds,
        parentId,
        position,
        expandParent: placement === "inside" && currentTarget.collapsed
      });
      loadOutlineHistory(workspaceIdRef.current).catch(toError(setError));
      restoreScroll();
    } catch (error) {
      setFlatState(before);
      setVisibleIds(computeVisibleIds(before));
      flatStateRef.current = before;
      setNodeSelection(selectionBefore, primaryBefore, anchorBefore);
      throw error;
    } finally {
      window.setTimeout(restoreScroll, 80);
    }
  };

  const selectWorkspace = useCallback((id: string) => {
    setMobileSidebarOpen(false);
    if (id === workspaceIdRef.current) return;
    pendingNodeRevealRef.current = null;
    workspaceIdRef.current = id;
    treeRequestRef.current += 1;
    tagsRequestRef.current += 1;
    tagResultsRequestRef.current += 1;
    systemTagGroupsRequestRef.current += 1;
    recycleBinRequestRef.current += 1;
    setWorkspaceId(id);
    setFlatState(null);
    setVisibleIds([]);
    setSingleSelectedId("");
    setTags([]);
    setActiveTagFilter("");
    setTagResults([]);
  }, [setSingleSelectedId]);

  const openOverdueNode = (node: OutlineNode) => {
    const nodeId = node.id;
    setSearch("");
    clearTagFilter();
    if (node.workspaceId !== workspaceIdRef.current) {
      selectWorkspace(node.workspaceId);
      pendingNodeRevealRef.current = { workspaceId: node.workspaceId, nodeId };
      return;
    }
    const current = flatStateRef.current;
    if (!current || !hasNode(current, nodeId)) return;
    const revealed = revealNodeInFlatTree(current, nodeId);
    setFlatState(revealed.state);
    setVisibleIds(revealed.visibleIds);
    flatStateRef.current = revealed.state;
    setSingleSelectedId(nodeId);
    window.setTimeout(() => {
      if (revealed.index >= 0) rowVirtualizer.scrollToIndex(revealed.index, { align: "center" });
      window.setTimeout(() => focusTitleInput(inputRefs.current.get(nodeId)), 30);
    }, 0);
  };

  const restoreRecycleBinEntry = async (entry: RecycleBinEntry) => {
    if (emptyingRecycleBin) return;
    setRestoringRecycleBinIds(current => new Set(current).add(entry.node.id));
    try {
      await apiPost<OutlineTreeNode>(`/api/nodes/${entry.node.id}/restore`, {});
      setRecycleBinEntries(current => current.filter(item => item.node.id !== entry.node.id));
    } finally {
      setRestoringRecycleBinIds(current => {
        const next = new Set(current);
        next.delete(entry.node.id);
        return next;
      });
    }
  };

  const emptyRecycleBin = async () => {
    if (emptyingRecycleBin || restoringRecycleBinIds.size || !recycleBinEntries.length) return;
    if (!window.confirm("Permanently delete all outlines in Recycle Bin, including their children, across all workspaces? This includes items hidden by search. This cannot be undone; undo history in affected workspaces will also be cleared.")) return;
    setEmptyingRecycleBin(true);
    recycleBinRequestRef.current += 1;
    try {
      await apiDelete<{ deletedCount: number }>("/api/recycle-bin");
      recycleBinRequestRef.current += 1;
      setRecycleBinEntries([]);
      setPendingDelete(null);
      await loadRecycleBin();
    } finally {
      setEmptyingRecycleBin(false);
    }
  };

  const splitNodeByLineBreaks = async (node: FlatNodeData, title: string) => {
    const currentWorkspaceId = workspaceIdRef.current;
    await apiPost(`/api/nodes/${node.id}/split-lines`, { title });
    if (workspaceIdRef.current !== currentWorkspaceId) return;
    await Promise.all([
      loadTree(currentWorkspaceId, { preserveSelection: true }),
      loadOutlineHistory(currentWorkspaceId)
    ]);
  };

  const createWorkspace = async (folderId?: string | null, parentWorkspaceId?: string | null) => {
    const created = await apiPost<Workspace>(
      "/api/workspaces",
      createWorkspaceRequestBody(selectedWorkspace, folderId, parentWorkspaceId)
    );
    await loadWorkspaces();
    selectWorkspace(created.id);
  };

  const convertNodeToWorkspace = async () => {
    const candidate = convertWorkspaceCandidate;
    if (!candidate || isConvertingWorkspace) return;
    setIsConvertingWorkspace(true);
    try {
      const created = await apiPost<Workspace>(`/api/nodes/${candidate.id}/convert-to-workspace`, {
        name: candidate.title
      });
      setConvertWorkspaceCandidate(null);
      setCollapsedWorkspaceIds(current => {
        const next = new Set(current);
        next.delete(workspaceIdRef.current);
        return next;
      });
      await loadWorkspaces();
      selectWorkspace(created.id);
    } finally {
      setIsConvertingWorkspace(false);
    }
  };

  const moveOutlinesToWorkspace = async (ids: string[], targetWorkspaceId: string, sourceWorkspaceId: string) => {
    if (!ids.length || sourceWorkspaceId === targetWorkspaceId || isMovingToWorkspace) return;
    setIsMovingToWorkspace(true);
    try {
      if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
      if (pendingNodeCreateCountRef.current > 0) {
        await new Promise<void>(resolve => nodeCreateWaitersRef.current.add(resolve));
      }
      await Promise.all([...nodePatchQueuesRef.current.values()]);
      const persistedIds = ids.map(id => resolvedTempNodeIdsRef.current.get(id) ?? id);
      if (persistedIds.some(id => id.startsWith("temp-"))) {
        throw new Error("The outline could not be saved. Please try moving it again.");
      }
      await apiPost("/api/nodes/move-to-workspace", {
        ids: persistedIds,
        workspaceId: targetWorkspaceId
      });
      if (workspaceIdRef.current === sourceWorkspaceId) {
        setSearch("");
        selectWorkspace(targetWorkspaceId);
        pendingNodeRevealRef.current = { workspaceId: targetWorkspaceId, nodeId: persistedIds[0] };
      }
    } finally {
      setIsMovingToWorkspace(false);
    }
  };

  const createWorkspaceFolder = async () => {
    const created = await apiPost<WorkspaceFolder>("/api/workspace-folders", { name: "New Folder" });
    setWorkspaceFolders(current => [...current, created]);
  };

  const updateWorkspaceName = async (workspace: Workspace, name: string) => {
    const trimmed = name.trim();
    if (!trimmed) {
      await loadWorkspaces();
      return;
    }
    const updated = await apiPatch<Workspace>(`/api/workspaces/${workspace.id}`, { name: trimmed });
    setWorkspaces(current => current.map(item => (item.id === updated.id ? updated : item)));
    if (updated.id === workspaceIdRef.current) await loadTree(updated.id, { preserveSelection: true });
  };

  const updateWorkspaceDraft = (id: string, name: string) => {
    setWorkspaces(current => current.map(workspace => (workspace.id === id ? { ...workspace, name } : workspace)));
  };

  const moveWorkspaceOptimistically = async (
    workspace: Workspace,
    nextFolderId: string | null,
    nextParentWorkspaceId: string | null,
    position: number
  ) => {
    const nextPosition =
      workspace.folderId === nextFolderId && workspace.parentWorkspaceId === nextParentWorkspaceId && workspace.position < position
        ? position - 1
        : position;
    if (
      workspace.folderId === nextFolderId &&
      workspace.parentWorkspaceId === nextParentWorkspaceId &&
      workspace.position === nextPosition
    ) return;
    const before = workspaces;
    setWorkspaces(current => reorderWorkspaces(current, workspace.id, nextFolderId, nextParentWorkspaceId, nextPosition));
    try {
      await apiPatch<Workspace>(`/api/workspaces/${workspace.id}`, {
        folderId: nextFolderId,
        parentWorkspaceId: nextParentWorkspaceId,
        position: nextPosition
      });
      await loadWorkspaces();
    } catch (error) {
      setWorkspaces(before);
      throw error;
    }
  };

  const startWorkspaceDrag = (workspace: Workspace, event: PointerEvent<HTMLSpanElement>) => {
    if (sidebarCollapsed) return;
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    workspaceDragTargetRef.current = null;
    setWorkspaceDragTarget(null);
    const startX = event.clientX;
    const startY = event.clientY;
    let isDragging = false;

    const move = (pointerEvent: globalThis.PointerEvent) => {
      if (!isDragging) {
        if (Math.hypot(pointerEvent.clientX - startX, pointerEvent.clientY - startY) < 5) return;
        isDragging = true;
        workspaceDragTargetRef.current = {
          folderId: workspace.folderId,
          parentWorkspaceId: workspace.parentWorkspaceId,
          markerId: workspace.parentWorkspaceId ?? workspace.folderId ?? "root",
          position: Number.MAX_SAFE_INTEGER
        };
        setWorkspaceDragTarget(workspaceDragTargetRef.current);
        document.body.classList.add("isDraggingWorkspace");
      }
      const workspaceElement = document
        .elementFromPoint(pointerEvent.clientX, pointerEvent.clientY)
        ?.closest<HTMLElement>("[data-workspace-drop-id]");
      if (workspaceElement && workspaceElement.dataset.workspaceDropId !== workspace.id) {
        const placement = getWorkspaceDropPlacement(workspaceElement, pointerEvent.clientY);
        const parentWorkspaceId = placement === "inside"
          ? workspaceElement.dataset.workspaceDropId ?? null
          : workspaceElement.dataset.workspaceParentId || null;
        if (
          parentWorkspaceId &&
          (parentWorkspaceId === workspace.id || isWorkspaceDescendant(workspaces, parentWorkspaceId, workspace.id))
        ) {
          workspaceDragTargetRef.current = null;
          setWorkspaceDragTarget(null);
          return;
        }
        const position = Number(workspaceElement.dataset.workspacePosition ?? "0") + (placement === "after" ? 1 : 0);
        workspaceDragTargetRef.current = {
          folderId: parentWorkspaceId ? null : workspaceElement.dataset.workspaceFolderId || null,
          parentWorkspaceId,
          markerId: workspaceElement.dataset.workspaceDropId ?? "",
          overWorkspaceId: workspaceElement.dataset.workspaceDropId,
          placement,
          position
        };
        setWorkspaceDragTarget(workspaceDragTargetRef.current);
        return;
      }

      const targetElement = document
        .elementFromPoint(pointerEvent.clientX, pointerEvent.clientY)
        ?.closest<HTMLElement>("[data-workspace-folder-drop-id]");
      const targetId = targetElement?.dataset.workspaceFolderDropId ?? null;
      workspaceDragTargetRef.current =
        targetId === null
          ? null
          : {
              folderId: targetId === "root" ? null : targetId,
              parentWorkspaceId: null,
              markerId: targetId,
              position: Number.MAX_SAFE_INTEGER
            };
      setWorkspaceDragTarget(workspaceDragTargetRef.current);
    };

    const end = () => {
      const target = workspaceDragTargetRef.current;
      workspaceDragTargetRef.current = null;
      setWorkspaceDragTarget(null);
      document.body.classList.remove("isDraggingWorkspace");
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", end);
      window.removeEventListener("pointercancel", end);
      if (isDragging && target) {
        moveWorkspaceOptimistically(workspace, target.folderId, target.parentWorkspaceId, target.position).catch(toError(setError));
      }
    };

    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", end);
    window.addEventListener("pointercancel", end);
  };

  const updateWorkspaceFolderName = async (folder: WorkspaceFolder, name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const updated = await apiPatch<WorkspaceFolder>(`/api/workspace-folders/${folder.id}`, { name: trimmed });
    setWorkspaceFolders(current => current.map(item => (item.id === updated.id ? updated : item)));
  };

  const updateWorkspaceFolderDraft = (id: string, name: string) => {
    setWorkspaceFolders(current => current.map(folder => (folder.id === id ? { ...folder, name } : folder)));
  };

  const toggleWorkspaceFolder = (id: string) => {
    setCollapsedWorkspaceFolderIds(current => nextCollapsedWorkspaceFolderIds(current, id));
  };

  const deleteWorkspaceFolder = async (folder: WorkspaceFolder) => {
    if (!window.confirm(`Delete folder "${folder.name}"? Workspaces inside it will move to root.`)) return;
    await apiDelete(`/api/workspace-folders/${folder.id}`);
    setWorkspaceFolders(current => current.filter(item => item.id !== folder.id));
    setCollapsedWorkspaceFolderIds(current => {
      const next = new Set(current);
      next.delete(folder.id);
      return next;
    });
    setWorkspaces(current =>
      current.map(workspace => (workspace.folderId === folder.id ? { ...workspace, folderId: null } : workspace))
    );
  };

  const deleteWorkspace = async (workspace: Workspace) => {
    if (!window.confirm(`Delete workspace "${workspace.name}" and all its sub-workspaces? All outlines in these workspaces will be permanently deleted. This cannot be undone.`)) return;
    const selectedWorkspaceId = workspaceIdRef.current;
    const wasSelected = workspace.id === selectedWorkspaceId
      || isWorkspaceDescendant(workspaces, selectedWorkspaceId, workspace.id);
    if (wasSelected) {
      treeRequestRef.current += 1;
      tagsRequestRef.current += 1;
      tagResultsRequestRef.current += 1;
    }
    await apiDelete(`/api/workspaces/${workspace.id}`);
    if (wasSelected && workspaceIdRef.current === selectedWorkspaceId) {
      selectWorkspace(nextWorkspaceIdAfterDelete(workspaces, workspace.id));
      setError("");
    }
    await loadWorkspaces();
  };

  const addTag = async (nodeId: string, nextName: string) => {
    const name = nextName.trim().replace(/^#/, "");
    const node = flatStateRef.current ? getNode(flatStateRef.current, nodeId) : undefined;
    if (!node || !name) throw new Error("The outline is no longer available.");
    if (node.tags.some(tag => tag.name === name)) return;
    const startedWorkspaceId = node.workspaceId;
    const existingTag = tags.find(tag => tag.name === name && tag.workspaceId === startedWorkspaceId);
    const optimisticTag: Tag = existingTag ?? {
      id: `temp-tag-${crypto.randomUUID()}`, workspaceId: startedWorkspaceId,
      name, color: "#9ca3af", createdAt: new Date().toISOString()
    };
    let persistedNodeId = nodeId;
    setFlatState(current => {
      if (!current) return current;
      const next = addOptimisticNodeTag(current, nodeId, optimisticTag);
      flatStateRef.current = next;
      return next;
    });
    try {
      if (nodeId.startsWith("temp-")) {
        await nodeCreateQueueRef.current;
        persistedNodeId = resolvedTempNodeIdsRef.current.get(nodeId) ?? "";
        if (!persistedNodeId) throw new Error("The new outline could not be saved. Please try adding the tag again.");
      }
      const savedTag = await apiPost<Tag>(`/api/nodes/${persistedNodeId}/tags`, { name });
      if (workspaceIdRef.current !== startedWorkspaceId) return;
      setTags(current => [...current.filter(tag => tag.id !== savedTag.id), savedTag]);
      setFlatState(current => {
        if (!current) return current;
        const localId = hasNode(current, persistedNodeId) ? persistedNodeId : nodeId;
        const next = reconcileOptimisticNodeTag(current, localId, optimisticTag.id, savedTag);
        flatStateRef.current = next;
        return next;
      });
    } catch (error) {
      if (workspaceIdRef.current === startedWorkspaceId) {
        setFlatState(current => {
          if (!current) return current;
          const localId = hasNode(current, persistedNodeId) ? persistedNodeId : nodeId;
          const next = removeOptimisticNodeTag(current, localId, optimisticTag.id);
          flatStateRef.current = next;
          return next;
        });
      }
      throw error;
    }
  };

  const unlinkNodeTag = async (nodeId: string, tag: Tag) => {
    const startedWorkspaceId = workspaceId;
    await apiDelete(`/api/nodes/${nodeId}/tags/${tag.id}`);
    if (workspaceIdRef.current !== startedWorkspaceId) return;
    setFlatState(current => {
      if (!current) return current;
      const next = removeOptimisticNodeTag(current, nodeId, tag.id);
      flatStateRef.current = next;
      return next;
    });
  };

  const renameSystemTag = async (previous: string, name: string) => {
    await apiPatch(`/api/system/tags/${encodeURIComponent(previous)}`, { name });
    setCollapsedSystemTags(current => {
      const next = new Set(current);
      if (next.delete(previous)) next.add(name.trim().replace(/^#/, ""));
      return next;
    });
    await Promise.all([loadTags(workspaceIdRef.current), loadSystemTagGroups()]);
  };

  const deleteSystemTag = async (name: string) => {
    if (!window.confirm(`Delete #${name} from all workspaces? Outline text will be kept.`)) return;
    await apiDelete(`/api/system/tags/${encodeURIComponent(name)}`);
    await Promise.all([loadTags(workspaceIdRef.current), loadSystemTagGroups()]);
  };

  const renderWorkspaceItem = (workspace: Workspace, depth = 0): React.ReactNode => {
    const children = workspacesByParent.get(workspace.id) ?? [];
    const isCollapsed = collapsedWorkspaceIds.has(workspace.id);

    return (
      <React.Fragment key={workspace.id}>
        <div
          className={[
            "workspaceItem",
            workspace.id === workspaceId && "active",
            dragState?.overWorkspaceId === workspace.id && "outlineDropTarget",
            workspaceDragTarget?.overWorkspaceId === workspace.id &&
              workspaceDragTarget.placement &&
              `drop-${workspaceDragTarget.placement}`
          ]
            .filter(Boolean)
            .join(" ")}
          title={sidebarCompact ? workspace.name : undefined}
          data-workspace-drop-id={workspace.id}
          data-workspace-folder-id={workspace.folderId ?? ""}
          data-workspace-parent-id={workspace.parentWorkspaceId ?? ""}
          data-workspace-position={workspace.position}
          style={{ "--workspace-depth": depth } as CSSProperties}
          onClick={() => selectWorkspace(workspace.id)}
        >
          {!sidebarCompact && (
            <button
              className="workspaceDisclosure"
              type="button"
              disabled={children.length === 0}
              title={isCollapsed ? "Expand workspace" : "Collapse workspace"}
              aria-label={isCollapsed ? "Expand workspace" : "Collapse workspace"}
              aria-expanded={children.length > 0 ? !isCollapsed : undefined}
              onClick={event => {
                event.stopPropagation();
                if (children.length > 0) setCollapsedWorkspaceIds(current => nextCollapsedWorkspaceIds(current, workspace.id));
              }}
            >
              {children.length > 0 && (isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />)}
            </button>
          )}
          <span
            className="workspaceIcon workspaceDragHandle"
            title={sidebarCompact ? workspace.name : "Drag workspace"}
            onPointerDown={event => startWorkspaceDrag(workspace, event)}
          >
            <WorkspaceIcon
              name={workspace.icon}
              size={15}
              strokeWidth={2.2}
            />
          </span>
          {!sidebarCompact && (
            <input
              value={workspace.name}
              onChange={event => updateWorkspaceDraft(workspace.id, event.target.value)}
              onBlur={event => updateWorkspaceName(workspace, event.target.value).catch(toError(setError))}
              onFocus={() => selectWorkspace(workspace.id)}
              onKeyDown={event => {
                if (shouldIgnoreTextInputKeyDown(event)) return;
                if (event.key === "Enter") event.currentTarget.blur();
              }}
            />
          )}
          {!sidebarCompact && (
            <button
              className="workspaceAddChildButton"
              type="button"
              title="New child workspace"
              onClick={event => {
                event.stopPropagation();
                createWorkspace(undefined, workspace.id).catch(toError(setError));
              }}
            >
              <Plus size={13} />
            </button>
          )}
          {!sidebarCompact && (
            <button
              className="workspaceDeleteButton"
              type="button"
              title="Delete workspace"
              onClick={event => {
                event.stopPropagation();
                deleteWorkspace(workspace).catch(toError(setError));
              }}
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
        {!isCollapsed && children.map(child => renderWorkspaceItem(child, depth + 1))}
      </React.Fragment>
    );
  };

  return (
    <div
      className={`appShell${sidebarCompact ? " sidebarCollapsed" : ""}${mobileSidebarOpen ? " mobileSidebarOpen" : ""}`}
      style={{ "--sidebar-width": `${sidebarWidth}px` } as CSSProperties}
      onPointerDownCapture={event => {
        if (!(event.target as HTMLElement).closest(".outlineList")) {
          multiSelectionKeyboardActiveRef.current = false;
        }
      }}
    >
      {mobileSidebarOpen && (
        <button className="sidebarBackdrop" type="button" aria-label="Close sidebar" onClick={() => setMobileSidebarOpen(false)} />
      )}
      <aside className="sidebar" id="workspace-sidebar" aria-label="Workspace navigation">
        <div className="sidebarHeader">
          <div className="brand">
            <span className="brandMark">
              <FolderTree size={18} />
            </span>
            {!sidebarCompact && <span>OpenOutliner</span>}
            <button
              className="collapseButton"
              type="button"
              onClick={() => mobileSidebarOpen ? setMobileSidebarOpen(false) : setSidebarCollapsed(collapsed => !collapsed)}
              title={mobileSidebarOpen ? "Close sidebar" : sidebarCompact ? "Expand sidebar" : "Collapse sidebar"}
            >
              {sidebarCompact ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
            </button>
          </div>
          {sidebarCompact && (
            <button
              className="sidebarCollapsedAdd"
              type="button"
              onClick={() => createWorkspace(null, null).catch(toError(setError))}
              title="New Workspace"
            >
              <Plus size={15} />
            </button>
          )}
        </div>

        {sidebarCompact ? (
          <button
            className="sidebarIconButton sidebarSearchToggle"
            type="button"
            title="Search"
            aria-label="Search"
            onClick={() => {
              setSidebarCollapsed(false);
              window.requestAnimationFrame(() => sidebarSearchRef.current?.focus());
            }}
          >
            <Search size={17} />
          </button>
        ) : (
          <div className="searchBox sidebarSearch">
            <Search size={16} aria-hidden="true" />
            <input
              ref={sidebarSearchRef}
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="Search"
              aria-label="Search"
            />
            {search && (
              <button className="searchClearButton" type="button" title="Clear search" aria-label="Clear search" onClick={() => setSearch("")}>
                <X size={14} />
              </button>
            )}
          </div>
        )}

        <div className="workspaceGroup">
          <button
            className={isSystemTagsWorkspace ? "systemWorkspaceItem active" : "systemWorkspaceItem"}
            type="button"
            title={sidebarCompact ? "Tags" : "System workspace"}
            onClick={() => selectWorkspace(SYSTEM_TAGS_WORKSPACE_ID)}
          >
            <span className="workspaceIcon" aria-hidden="true">
              <TagsIcon size={15} strokeWidth={2.2} />
            </span>
            {!sidebarCompact && (
              <span className="systemWorkspaceLabel">
                <span>Tags</span>
                <small className="systemWorkspaceBadge">System</small>
              </span>
            )}
          </button>
          <button
            className={isRecycleBinWorkspace ? "systemWorkspaceItem active" : "systemWorkspaceItem"}
            type="button"
            title={sidebarCompact ? "Recycle Bin" : "Completed and deleted outlines"}
            onClick={() => selectWorkspace(SYSTEM_RECYCLE_BIN_WORKSPACE_ID)}
          >
            <span className="workspaceIcon" aria-hidden="true">
              <Trash2 size={15} strokeWidth={2.2} />
            </span>
            {!sidebarCompact && (
              <span className="systemWorkspaceLabel">
                <span>Recycle Bin</span>
                <small className={recycleBinEntries.length ? undefined : "systemWorkspaceBadge"}>{recycleBinEntries.length || "System"}</small>
              </span>
            )}
          </button>
          {!sidebarCompact ? (
            <>
              <div className="sidebarLabel workspaceLabel">
                <span>Workspaces</span>
                <button type="button" title="New Workspace" aria-label="New Workspace" onClick={() => createWorkspace(null, null).catch(toError(setError))}>
                  <Plus size={14} />
                </button>
              </div>
              <div
                className={workspaceDragTarget?.markerId === "root" ? "workspaceRootDrop active" : "workspaceRootDrop"}
                data-workspace-folder-drop-id="root"
              >
                {rootWorkspaces.map(workspace => renderWorkspaceItem(workspace))}
              </div>
              <div className="sidebarLabel workspaceLabel foldersLabel">
                <span>Folders</span>
                <button type="button" title="New folder" aria-label="New folder" onClick={() => createWorkspaceFolder().catch(toError(setError))}>
                  <FolderPlus size={14} />
                </button>
              </div>
              {workspaceFolders.map(folder => {
                const isCollapsed = collapsedWorkspaceFolderIds.has(folder.id);
                const folderWorkspaces = workspacesByFolder.get(folder.id) ?? [];
                return (
                  <div
                    className={
                      workspaceDragTarget?.markerId === folder.id ? "workspaceFolder dropActive" : "workspaceFolder"
                    }
                    key={folder.id}
                    data-workspace-folder-drop-id={folder.id}
                  >
                    <div className="workspaceFolderHeader">
                      <button
                        type="button"
                        className="workspaceFolderIconButton"
                        title={isCollapsed ? "Expand folder" : "Collapse folder"}
                        aria-expanded={!isCollapsed}
                        onClick={() => toggleWorkspaceFolder(folder.id)}
                      >
                        {isCollapsed ? (
                          <FolderClosed size={18} strokeWidth={2.2} />
                        ) : (
                          <FolderOpen size={18} strokeWidth={2.2} />
                        )}
                      </button>
                      <input
                        value={folder.name}
                        onChange={event => updateWorkspaceFolderDraft(folder.id, event.target.value)}
                        onBlur={event => updateWorkspaceFolderName(folder, event.target.value).catch(toError(setError))}
                        onKeyDown={event => {
                          if (shouldIgnoreTextInputKeyDown(event)) return;
                          if (event.key === "Enter") event.currentTarget.blur();
                        }}
                      />
                      <button
                        type="button"
                        title="New workspace in folder"
                        onClick={() => createWorkspace(folder.id).catch(toError(setError))}
                      >
                        <Plus size={13} />
                      </button>
                      <button
                        type="button"
                        title="Delete folder"
                        onClick={() => deleteWorkspaceFolder(folder).catch(toError(setError))}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                    {!isCollapsed && folderWorkspaces.map(workspace => renderWorkspaceItem(workspace))}
                    {!isCollapsed && folderWorkspaces.length === 0 && (
                      <div className="workspaceFolderEmpty">Empty folder</div>
                    )}
                  </div>
                );
              })}
            </>
          ) : (
            workspaces.filter(workspace => !workspace.parentWorkspaceId).map(workspace => renderWorkspaceItem(workspace))
          )}
        </div>
        <div className="sidebarFooter">
          <SyncPanel embedded compact={sidebarCompact} />
          <button
            className="sidebarIconButton"
            type="button"
            title="防窥：立即虚化"
            aria-label="立即虚化"
            onClick={activatePrivacyScreen}
          >
            <EyeOff size={17} />
          </button>
          <button
            className="sidebarIconButton"
            type="button"
            title="Markdown shortcuts"
            aria-label="Markdown shortcuts"
            onClick={() => {
              setMobileSidebarOpen(false);
              setIsMarkdownHelpOpen(true);
            }}
          >
            <CircleHelp size={17} />
          </button>
          <button
            className="sidebarIconButton themeToggle"
            type="button"
            title={`Theme: ${themeLabel(theme)}`}
            aria-label={`Theme: ${themeLabel(theme)}`}
            onClick={cycleTheme}
          >
            {theme === "light" ? <Sun size={17} /> : theme === "dark" ? <Moon size={17} /> : <Monitor size={17} />}
          </button>
        </div>
        {!sidebarCompact && (
          <div
            className="panelResizeHandle sidebarResizeHandle"
            role="separator"
            aria-label="Resize workspace sidebar"
            aria-orientation="vertical"
            aria-valuemin={MIN_SIDEBAR_WIDTH}
            aria-valuemax={MAX_SIDEBAR_WIDTH}
            aria-valuenow={sidebarWidth}
            tabIndex={0}
            title="Drag to resize; double-click to reset"
            onPointerDown={event => startPanelResize("sidebar", event)}
            onKeyDown={event => resizePanelWithKeyboard("sidebar", event)}
            onDoubleClick={() => setSidebarWidth(DEFAULT_SIDEBAR_WIDTH)}
          />
        )}
      </aside>

      <main className="mainPane">
        <button
          className="sidebarIconButton mobileSidebarToggle"
          type="button"
          title="Open sidebar"
          aria-label="Open sidebar"
          aria-expanded={mobileSidebarOpen}
          aria-controls="workspace-sidebar"
          onClick={() => setMobileSidebarOpen(true)}
        >
          <PanelLeft size={18} />
        </button>
        {isMarkdownHelpOpen && (
          <div className="modalBackdrop" role="presentation" onClick={() => setIsMarkdownHelpOpen(false)}>
            <div
              className="markdownHelpDialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby="markdown-help-title"
              onClick={event => event.stopPropagation()}
            >
              <div className="markdownHelpHeader">
                <h2 id="markdown-help-title">Markdown shortcuts</h2>
                <button type="button" onClick={() => setIsMarkdownHelpOpen(false)}>
                  <Check size={16} />
                </button>
              </div>
              <div className="markdownHelpList">
                <div>
                  <span>Bold</span>
                  <code>Ctrl+B</code>
                  <small>**text**</small>
                </div>
                <div>
                  <span>Italic</span>
                  <code>Ctrl+I</code>
                  <small>*text*</small>
                </div>
                <div>
                  <span>Strike</span>
                  <code>Ctrl+Alt+X</code>
                  <small>~~text~~</small>
                </div>
                <div>
                  <span>Inline code</span>
                  <code>Ctrl+E</code>
                  <small>`code`</small>
                </div>
                <div>
                  <span>Link text</span>
                  <code>Ctrl+K</code>
                  <small>[text](paste)</small>
                </div>
                <div>
                  <span>Highlight</span>
                  <code>Ctrl+Shift+H</code>
                  <small>==text==</small>
                </div>
                <div>
                  <span>Text color</span>
                  <code>Selection menu</code>
                  <small>{"{{color:red}}text{{/color}}"}</small>
                </div>
              </div>
            </div>
          </div>
        )}

        {convertWorkspaceCandidate && (
          <div
            className="modalBackdrop"
            role="presentation"
            onClick={() => {
              if (!isConvertingWorkspace) setConvertWorkspaceCandidate(null);
            }}
          >
            <div
              className="convertWorkspaceDialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby="convert-workspace-title"
              onClick={event => event.stopPropagation()}
            >
              <div className="convertWorkspaceIcon" aria-hidden="true">
                <FolderTree size={20} />
              </div>
              <div className="convertWorkspaceCopy">
                <h2 id="convert-workspace-title">Convert to workspace?</h2>
                <p>
                  <strong>{convertWorkspaceCandidate.title || "Untitled"}</strong> and all nested outlines will move into a new child workspace.
                </p>
                <small>The outline will no longer appear in the current workspace.</small>
              </div>
              <div className="convertWorkspaceActions">
                <button
                  type="button"
                  disabled={isConvertingWorkspace}
                  onClick={() => setConvertWorkspaceCandidate(null)}
                >
                  Cancel
                </button>
                <button
                  className="primary"
                  type="button"
                  disabled={isConvertingWorkspace}
                  onClick={() => convertNodeToWorkspace().catch(toError(setError))}
                >
                  {isConvertingWorkspace ? "Converting…" : "Convert"}
                </button>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="errorBar">
            <span>{error}</span>
            <button type="button" onClick={() => setError("")}>
              <Check size={16} />
            </button>
          </div>
        )}

        {pendingDelete && pendingDelete.workspaceId === workspaceId && outlineHistory.undoLabel === "Delete outline" && (
          <div className="undoBar" role="status" aria-live="polite">
            <span>{pendingDelete.action === "complete"
              ? pendingDelete.nodeCount === 1 ? "Completed outline" : `Completed ${pendingDelete.nodeCount} outlines`
              : pendingDelete.nodeCount === 1 ? "Deleted node" : `Deleted ${pendingDelete.nodeCount} nodes`}</span>
            <button type="button" onClick={() => undoPendingDelete().catch(toError(setError))}>
              <Undo2 size={15} />
              <span>Undo</span>
            </button>
          </div>
        )}

        <section
          className={isInspectorOpen && !isSystemTagsWorkspace && !isRecycleBinWorkspace ? "contentGrid" : "contentGrid commentsClosed"}
          ref={contentGridRef}
          style={{ "--inspector-width": `${inspectorWidth}px` } as CSSProperties}
        >
          <div className="outlineArtwork" aria-hidden="true" />
          <div className="outlineSurface" ref={outlineSurfaceRef} tabIndex={-1}
            onPointerDown={event => {
              if (event.target === event.currentTarget || event.target === virtualListRef.current
                || (event.target as HTMLElement).classList.contains("outlineList")) {
                event.currentTarget.focus({ preventScroll: true });
              }
            }}
            onKeyDownCapture={event => {
              if (isSystemTagsWorkspace || isRecycleBinWorkspace || isTagFiltering || !filteredNodes.length
                || !shouldHandleOutlineSelectAll(event, isEditableElement(event.target),
                  event.target instanceof HTMLElement && event.target.matches(".nodeTitle"))) return;
              event.preventDefault();
              event.stopPropagation();
              event.currentTarget.focus({ preventScroll: true });
              window.getSelection()?.removeAllRanges();
              const primaryId = filteredNodes.includes(selectedIdRef.current) ? selectedIdRef.current : filteredNodes[0];
              setNodeSelection(filteredNodes, primaryId, filteredNodes[0]);
            }}>
            <div className="outlineHeader">
              {isRecycleBinWorkspace ? (
                <div className="systemWorkspaceTitle">
                  <div>
                    <Trash2 size={21} strokeWidth={2.2} />
                    <h1>Recycle Bin</h1>
                  </div>
                  <span>Completed and deleted outlines</span>
                </div>
              ) : isSystemTagsWorkspace ? (
                <div className="systemWorkspaceTitle">
                  <div>
                    <TagsIcon size={21} strokeWidth={2.2} />
                    <h1>Tags</h1>
                  </div>
                  <span>System workspace · Auto-generated</span>
                </div>
              ) : isTagFiltering ? (
                <h1>{`#${activeTagFilter}`}</h1>
              ) : selectedWorkspace ? (
                <input
                  className="workspaceTitleInput"
                  aria-label="Workspace title"
                  title="Rename workspace"
                  value={selectedWorkspace.name}
                  onChange={event => updateWorkspaceDraft(selectedWorkspace.id, event.target.value)}
                  onBlur={event => updateWorkspaceName(selectedWorkspace, event.target.value).catch(toError(setError))}
                  onKeyDown={event => {
                    if (shouldIgnoreTextInputKeyDown(event)) return;
                    if (event.key === "Enter") event.currentTarget.blur();
                  }}
                />
              ) : (
                <h1>OpenOutliner</h1>
              )}
              {isTagFiltering && (
                <button className="tagFilterClear" type="button" onClick={clearTagFilter}>
                  <X size={15} />
                  <span>Clear</span>
                </button>
              )}
              {isRecycleBinWorkspace && (
                <button
                  className="recycleBinEmpty"
                  type="button"
                  disabled={emptyingRecycleBin || restoringRecycleBinIds.size > 0 || recycleBinEntries.length === 0}
                  onClick={() => emptyRecycleBin().catch(toError(setError))}
                >
                  <Trash2 size={15} />
                  <span>{emptyingRecycleBin ? "Deleting…" : "Delete all"}</span>
                </button>
              )}
            </div>
            <div className={selectedNodeIds.size > 1 ? "outlineList hasMultiSelection" : "outlineList"}>
              {visibleItemCount > 0 ? (
                <div
                  className="virtualOutlineList"
                  ref={virtualListRef}
                  style={{ height: `${rowVirtualizer.getTotalSize()}px` }}
                >
                  {virtualItems.map(virtualItem => {
                    if (isRecycleBinWorkspace) {
                      const entry = filteredRecycleBinEntries[virtualItem.index];
                      if (!entry) return null;
                      return (
                        <div
                          className="virtualOutlineRow"
                          data-index={virtualItem.index}
                          key={entry.node.id}
                          ref={rowVirtualizer.measureElement}
                          style={{ transform: `translateY(${virtualItem.start - listScrollMargin}px)` }}
                        >
                          <RecycleBinRow
                            entry={entry}
                            restoring={restoringRecycleBinIds.has(entry.node.id)}
                            disabled={emptyingRecycleBin}
                            onRestore={() => restoreRecycleBinEntry(entry).catch(toError(setError))}
                          />
                        </div>
                      );
                    }
                    if (isSystemTagsWorkspace) {
                      const row = systemTagRows[virtualItem.index];
                      if (!row) return null;
                      return (
                        <div
                          className="virtualOutlineRow"
                          data-index={virtualItem.index}
                          key={systemTagRowKey(row, virtualItem.index)}
                          ref={rowVirtualizer.measureElement}
                          style={{ transform: `translateY(${virtualItem.start - listScrollMargin}px)` }}
                        >
                          {row.kind === "tag" ? (
                            <SystemTagGroupRow
                              group={row.group}
                              onRename={name => renameSystemTag(row.group.name, name)}
                              onDelete={() => deleteSystemTag(row.group.name)}
                              collapsed={collapsedSystemTags.has(row.group.name)}
                              onToggle={() => setCollapsedSystemTags(current =>
                                nextCollapsedWorkspaceIds(current, row.group.name)
                              )}
                            />
                          ) : (
                            <SystemTaggedNodeRow
                              color={row.color}
                              result={row.result}
                              onOpen={() => openTagResult(row.result).catch(toError(setError))}
                            />
                          )}
                        </div>
                      );
                    }
                    if (isTagFiltering) {
                      const result = filteredTagResults[virtualItem.index];
                      if (!result) return null;
                      return (
                        <div
                          className="virtualOutlineRow"
                          data-index={virtualItem.index}
                          key={result.node.id}
                          ref={rowVirtualizer.measureElement}
                          style={{ transform: `translateY(${virtualItem.start - listScrollMargin}px)` }}
                        >
                          <TagResultRow
                            result={result}
                            selected={selectedId === result.node.id}
                            onOpen={() => openTagResult(result).catch(toError(setError))}
                            onTagClick={tag => loadTagResults(tag.name).catch(toError(setError))}
                          />
                        </div>
                      );
                    }

                    const nodeId = filteredNodes[virtualItem.index];
                    if (!nodeId) return null;
                    const node = flatState ? getNode(flatState, nodeId) : undefined;
                    if (!node) return null;
                    const depth = flatState ? getNodeDepth(flatState, nodeId) : 0;
                    const selectionPosition = getNodeSelectionPosition(
                      filteredNodes,
                      selectedNodeIds,
                      virtualItem.index
                    );
                    return (
                      <div
                        className="virtualOutlineRow"
                        data-index={virtualItem.index}
                        key={node.id}
                        ref={rowVirtualizer.measureElement}
                        style={{ transform: `translateY(${virtualItem.start - listScrollMargin}px)` }}
                      >
                        <NodeRow
                          node={node}
                          depth={depth}
                          selected={selectedNodeIds.has(node.id)}
                          selectionPosition={selectionPosition}
                          active={selectedId === node.id}
                          canDrag={!isTagFiltering && !isMovingToWorkspace}
                          dragging={draggingNodeIds.has(node.id)}
                          dropPlacement={dragState?.overId === node.id ? dragState.placement ?? null : null}
                          registerInput={element => {
                            if (element) inputRefs.current.set(node.id, element);
                            else inputRefs.current.delete(node.id);
                          }}
                          onMouseSelect={event => selectNodeWithMouse(node.id, event)}
                          onFocusSelect={() => {
                            editingNodeIdRef.current = node.id;
                            setSingleSelectedId(node.id);
                          }}
                          onContextSelect={() => {
                            if (!selectedNodeIdsRef.current.has(node.id)) setSingleSelectedId(node.id);
                          }}
                          onBlurFocus={() => {
                            if (editingNodeIdRef.current === node.id) editingNodeIdRef.current = "";
                          }}
                          onCompositionChange={isComposing => {
                            if (isComposing) nodeCompositionTracker.start(node.id);
                            else nodeCompositionTracker.finish(node.id);
                          }}
                          onSelectionStart={event => startNodeSelection(node, event)}
                          onPatchLocal={patch => {
                            const current = flatStateRef.current;
                            if (!current) return;
                            const next = updateNode(current, node.id, patch);
                            flatStateRef.current = next;
                            setFlatState(next);
                          }}
                          onCacheTitle={title => {
                            if (node.id.startsWith("temp-") || reconcilingNodeIdsRef.current.has(node.id)) {
                              localNodeTitlesRef.current.set(node.id, title);
                            }
                          }}
                          onCommit={patch => patchNode(node.id, patch).catch(toError(setError))}
                          onToggle={patch => {
                            setFlatState(s => {
                              if (!s) return s;
                              const next = updateNode(s, node.id, patch);
                              setVisibleIds(computeVisibleIds(next));
                              flatStateRef.current = next;
                              return next;
                            });
                            patchNode(node.id, patch).catch(toError(setError));
                          }}
                          onCreateAfter={(title, currentTitle) =>
                            createAfter(node, title, currentTitle).catch(toError(setError))
                          }
                          onCreateBefore={currentTitle =>
                            createBefore(node, currentTitle).catch(toError(setError))
                          }
                          onIndent={() => indent(node).catch(toError(setError))}
                          onOutdent={() => outdent(node).catch(toError(setError))}
                          onFocusPrevious={() => focusRelative(node, -1)}
                          onFocusNext={() => focusRelative(node, 1)}
                          onMoveStart={event => startNodeDrag(node, event)}
                          onTagClick={tag => loadTagResults(tag.name).catch(toError(setError))}
                          onTagRemove={tag => unlinkNodeTag(node.id, tag).catch(toError(setError))}
                          tagCatalog={tags}
                          onTagAdd={name => addTag(node.id, name)}
                          onTagError={toError(setError)}
                          onConvertToWorkspace={title => setConvertWorkspaceCandidate({ id: node.id, title })}
                          onSplitLines={title => splitNodeByLineBreaks(node, title)}
                          onComplete={() => deleteNodeOptimistically(node, "complete")}
                          onDelete={() => deleteNodeOptimistically(node)}
                        />
                      </div>
                    );
                  })}
                </div>
              ) : isRecycleBinWorkspace ? (
                <div className="outlineEmptyState">
                  {isSearching ? "No matching completed outlines" : "Recycle Bin is empty"}
                </div>
              ) : isSystemTagsWorkspace ? (
                <div className="outlineEmptyState">
                  {isSearching ? "No matching tags or nodes" : "No tagged nodes yet"}
                </div>
              ) : visibleIds.length === 0 && flatState ? (
                <button
                  className="emptyNodeButton"
                  type="button"
                  onClick={() => createFirstNode().catch(toError(setError))}
                >
                  <Plus size={16} />
                  <span>First node</span>
                </button>
              ) : (
                <div className="outlineEmptyState">{isTagFiltering ? "No tagged nodes" : "No matching nodes"}</div>
              )}
            </div>
          </div>

          {isInspectorOpen && !isSystemTagsWorkspace && !isRecycleBinWorkspace && (
            <aside className="inspector">
              <div
                className="panelResizeHandle inspectorResizeHandle"
                role="separator"
                aria-label="Resize comments sidebar"
                aria-orientation="vertical"
                aria-valuemin={MIN_INSPECTOR_WIDTH}
                aria-valuemax={MAX_INSPECTOR_WIDTH}
                aria-valuenow={inspectorWidth}
                tabIndex={0}
                title="Drag to resize; double-click to reset"
                onPointerDown={event => startPanelResize("inspector", event)}
                onKeyDown={event => resizePanelWithKeyboard("inspector", event)}
                onDoubleClick={() => setInspectorWidth(DEFAULT_INSPECTOR_WIDTH)}
              />
              <div className="inspectorHeader">
                <div>
                  <span>Comments</span>
                </div>
                <button
                  className="iconButton commentsHideButton"
                  type="button"
                  title="Hide comments"
                  onClick={() => setIsInspectorOpen(false)}
                >
                  <PanelRight size={17} />
                </button>
              </div>
              {selectedNode ? (
                <>
                  <div className="notesAlert">
                    <CircleCheck className="notesAlertIcon" size={18} strokeWidth={2.2} />
                    <div className="notesAlertContent">
                      <div className="notesAlertTitle">Notes</div>
                      <NodeNotes key={selectedNode.id} node={selectedNode} onError={toError(setError)} onTemporaryChange={body => {
                        const current = flatStateRef.current;
                        if (!current) return;
                        const next = updateNode(current, selectedNode.id, { body });
                        flatStateRef.current = next;
                        setFlatState(next);
                      }} />
                    </div>
                  </div>
                </>
              ) : (
                <div className="emptyInspector">No node selected</div>
              )}
              <OverdueTasks nodes={overdueNodes} workspaces={workspaces} onOpen={openOverdueNode} />
            </aside>
          )}
          {!isInspectorOpen && !isSystemTagsWorkspace && !isRecycleBinWorkspace && (
            <button
              className="commentsRestoreButton"
              type="button"
              title="Show comments"
              onClick={() => setIsInspectorOpen(true)}
            >
              <PanelRight size={15} />
              <span>Comments</span>
            </button>
          )}
        </section>
      </main>
      {dragState && (
        <div
          className="dragPreview"
          style={{ transform: `translate3d(${dragState.x + 12}px, ${dragState.y + 12}px, 0)` }}
        >
          <span className="nodeDot" />
          <span>{dragState.title || "Untitled"}{outlineDropWorkspace ? ` → ${outlineDropWorkspace.name}` : ""}</span>
        </div>
      )}
    </div>
  );
}

function OverdueTasks({ nodes, workspaces, onOpen }: {
  nodes: OutlineNode[];
  workspaces: Workspace[];
  onOpen: (node: OutlineNode) => void;
}) {
  const [collapsed, setCollapsed] = useState(false);
  return (
    <section className="overduePanel" aria-labelledby="overdue-panel-title">
      <button
        className="overdueHeader"
        type="button"
        title="Unfinished tasks across all pages due by tomorrow, including overdue tasks"
        aria-expanded={!collapsed}
        onClick={() => setCollapsed(current => !current)}
      >
        <span>
          {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
          <CalendarX2 size={16} />
          <strong id="overdue-panel-title">Overdue</strong>
        </span>
        <small>{nodes.length}</small>
      </button>
      {!collapsed && nodes.length > 0 ? (
        <div className="overdueList">
          {nodes.map(node => (
            <button type="button" key={node.id} onClick={() => onOpen(node)}
              title={`${workspaces.find(workspace => workspace.id === node.workspaceId)?.name ?? ""} / ${node.title || "Untitled"}`}>
              <span>{node.title || "Untitled"}</span>
              <time dateTime={node.dueDate ?? undefined}>
                {node.dueDate ? new Date(`${node.dueDate}T00:00:00`).toLocaleDateString(undefined, {
                  month: "short",
                  day: "numeric"
                }) : ""}
              </time>
            </button>
          ))}
        </div>
      ) : !collapsed ? (
        <p className="overdueEmpty">No tasks due by tomorrow</p>
      ) : null}
    </section>
  );
}

function NodeNotes({ node, onError, onTemporaryChange }: { node: FlatNodeData; onError: (error: unknown) => void; onTemporaryChange: (body: string) => void }) {
  const [value, setValue] = useState(() => readNodeDraft(node.id, "body") ?? node.body);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  useEffect(() => {
    const resolved = (event: Event) => {
      const detail = (event as CustomEvent<{ nodeId: string; field: string; value?: string }>).detail;
      if (detail.nodeId === node.id && detail.field === "body" && detail.value !== undefined) setValue(detail.value);
    };
    window.addEventListener("outliner-draft-resolved", resolved);
    return () => window.removeEventListener("outliner-draft-resolved", resolved);
  }, [node.id]);
  useEffect(() => {
    if (document.activeElement !== inputRef.current) setValue(readNodeDraft(node.id, "body") ?? node.body);
  }, [node.id, node.body]);
  return <textarea
    ref={inputRef}
    className="nodeNotes"
    value={value}
    onFocus={() => { if (!node.id.startsWith("temp-")) beginNodeEdit(node.id, "body", node.body); }}
    onChange={event => {
      setValue(event.target.value);
      if (node.id.startsWith("temp-")) onTemporaryChange(event.target.value);
      else stageNodeDraft(node.id, "body", event.target.value, node.body);
    }}
    onBlur={() => { if (!node.id.startsWith("temp-")) void endNodeEdit(node.id, "body").catch(onError); }}
    placeholder="Add node details"
  />;
}

function NodeRow({
  node,
  depth,
  selected,
  selectionPosition,
  active,
  canDrag,
  dragging,
  dropPlacement,
  registerInput,
  onMouseSelect,
  onFocusSelect,
  onContextSelect,
  onBlurFocus,
  onCompositionChange,
  onSelectionStart,
  onPatchLocal,
  onCacheTitle,
  onCommit,
  onToggle,
  onCreateAfter,
  onCreateBefore,
  onIndent,
  onOutdent,
  onFocusPrevious,
  onFocusNext,
  onMoveStart,
  onTagClick,
  onTagRemove,
  tagCatalog,
  onTagAdd,
  onTagError,
  onConvertToWorkspace,
  onSplitLines,
  onComplete,
  onDelete
}: {
  node: FlatNodeData;
  depth: number;
  selected: boolean;
  selectionPosition: NodeSelectionPosition | null;
  active: boolean;
  canDrag: boolean;
  dragging: boolean;
  dropPlacement: DropPlacement | null;
  registerInput: (element: HTMLTextAreaElement | null) => void;
  onMouseSelect: (event: MouseEvent<HTMLElement>) => boolean;
  onFocusSelect: () => void;
  onContextSelect: () => void;
  onBlurFocus: () => void;
  onCompositionChange: (isComposing: boolean) => void;
  onSelectionStart: (event: PointerEvent<HTMLDivElement>) => void;
  onPatchLocal: (patch: Partial<FlatNodeData>) => void;
  onCacheTitle: (title: string) => void;
  onCommit: (patch: Partial<FlatNodeData>) => void;
  onToggle: (patch: Partial<FlatNodeData>) => void;
  onCreateAfter: (title?: string, currentTitle?: string) => void;
  onCreateBefore: (currentTitle?: string) => void;
  onIndent: () => void;
  onOutdent: () => void;
  onFocusPrevious: () => void;
  onFocusNext: () => void;
  onMoveStart: (event: PointerEvent<HTMLButtonElement>) => void;
  onTagClick: (tag: Tag) => void;
  onTagRemove: (tag: Tag) => void;
  tagCatalog: Tag[];
  onTagAdd: (name: string) => Promise<void>;
  onTagError: (error: unknown) => void;
  onConvertToWorkspace: (title: string) => void;
  onSplitLines: (title: string) => Promise<void>;
  onComplete: () => Promise<void>;
  onDelete: () => Promise<void>;
}) {
  const titleInputRef = useRef<HTMLTextAreaElement | null>(null);
  const inlineTagSelectionRef = useRef<{ value: string; start: number } | null>(null);
  const titleMeasureRef = useRef<HTMLDivElement | null>(null);
  const dateInputRef = useRef<HTMLInputElement | null>(null);
  const markdownMenuRef = useRef<HTMLDivElement | null>(null);
  const nodeContextMenuRef = useRef<HTMLDivElement | null>(null);
  const onCompositionChangeRef = useRef(onCompositionChange);
  onCompositionChangeRef.current = onCompositionChange;
  const [localTitle, setLocalTitle] = useState(() => readNodeDraft(node.id, "title") ?? node.title);
  const lastStagedTitleRef = useRef(localTitle);
  const [markdownMenu, setMarkdownMenu] = useState<MarkdownContextMenuState | null>(null);
  const [nodeContextMenu, setNodeContextMenu] = useState<NodeContextMenuState | null>(null);
  const syncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const resolved = (event: Event) => {
      const detail = (event as CustomEvent<{ nodeId: string; field: string; value?: string }>).detail;
      if (detail.nodeId !== node.id || detail.field !== "title" || detail.value === undefined) return;
      if (syncTimerRef.current) { clearTimeout(syncTimerRef.current); syncTimerRef.current = null; }
      lastStagedTitleRef.current = detail.value;
      setLocalTitle(detail.value);
    };
    window.addEventListener("outliner-draft-resolved", resolved);
    return () => window.removeEventListener("outliner-draft-resolved", resolved);
  }, [node.id]);
  const textSelectionPointerRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    startOffset: number;
  } | null>(null);

  // Sync external title changes (drag, undo, etc.) into local state
  useEffect(() => {
    if (document.activeElement === titleInputRef.current) return;
    const next = readNodeDraft(node.id, "title") ?? node.title;
    lastStagedTitleRef.current = next;
    setLocalTitle(next);
  }, [node.title]);

  const stageTitle = useCallback((title: string) => {
    onCacheTitle(title);
    if (title === lastStagedTitleRef.current) return;
    lastStagedTitleRef.current = title;
    if (!node.id.startsWith("temp-")) stageNodeDraft(node.id, "title", title, node.title);
  }, [node.id, node.title, onCacheTitle]);

  // Flush local title to global state
  const flushTitle = useCallback((title: string) => {
    if (syncTimerRef.current) {
      clearTimeout(syncTimerRef.current);
      syncTimerRef.current = null;
    }
    stageTitle(title);
    onPatchLocal({ title });
  }, [stageTitle, onPatchLocal]);

  // Debounced sync during typing
  const syncTitleDebounced = useCallback((title: string) => {
    stageTitle(title);
    if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    syncTimerRef.current = setTimeout(() => {
      onPatchLocal({ title });
      syncTimerRef.current = null;
    }, 300);
  }, [stageTitle, onPatchLocal]);

  const inlineTags = useInlineTagInput({
    nodeId: node.id, value: localTitle, inputRef: titleInputRef, tags: tagCatalog,
    onAdd: onTagAdd, onError: onTagError,
    onApply: (title, selectionStart) => {
      inlineTagSelectionRef.current = { value: title, start: selectionStart };
      setLocalTitle(title);
      flushTitle(title);
    }
  });

  useLayoutEffect(() => {
    const selection = inlineTagSelectionRef.current;
    const input = titleInputRef.current;
    if (selection && input && document.activeElement === input && input.value === selection.value) {
      input.setSelectionRange(selection.start, selection.start);
      resizeTitleInput(input);
    }
    inlineTagSelectionRef.current = null;
  }, [localTitle]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
      onCompositionChangeRef.current(false);
    };
  }, []);

  useEffect(() => {
    if (!markdownMenu && !nodeContextMenu) return;
    const closeOnPointerDown = (event: globalThis.PointerEvent) => {
      const target = event.target as Node;
      if (markdownMenu && !markdownMenuRef.current?.contains(target)) setMarkdownMenu(null);
      if (nodeContextMenu && !nodeContextMenuRef.current?.contains(target)) setNodeContextMenu(null);
    };
    const closeMenus = () => {
      setMarkdownMenu(null);
      setNodeContextMenu(null);
    };
    const closeOnWindowBlur = () => {
      if (markdownMenu) return;
      closeMenus();
    };
    const closeOnScroll = (event: Event) => {
      const target = event.target;
      if (markdownMenu && target instanceof Node && markdownMenuRef.current?.contains(target)) return;
      closeMenus();
    };
    const closeOnEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") closeMenus();
    };
    document.addEventListener("pointerdown", closeOnPointerDown);
    window.addEventListener("blur", closeOnWindowBlur);
    window.addEventListener("resize", closeMenus);
    window.addEventListener("scroll", closeOnScroll, true);
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnPointerDown);
      window.removeEventListener("blur", closeOnWindowBlur);
      window.removeEventListener("resize", closeMenus);
      window.removeEventListener("scroll", closeOnScroll, true);
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [markdownMenu, nodeContextMenu]);

  const commitMarkdownEdit = (
    nextTitle: string,
    selectionStart: number,
    selectionEnd: number,
    restoreFocus = true
  ) => {
    setLocalTitle(nextTitle);
    flushTitle(nextTitle);
    if (!node.id.startsWith("temp-")) void flushNodeDraft(node.id, "title").catch(() => {});
    setMarkdownMenu(null);
    if (!restoreFocus) return;
    window.setTimeout(() => {
      const input = titleInputRef.current;
      if (!input) return;
      focusTitleInput(input);
      input.setSelectionRange(selectionStart, selectionEnd);
    }, 0);
  };

  const applyMarkdownStyleFromMenu = (style: MarkdownStyle) => {
    if (!markdownMenu) return;
    const result = applyMarkdownStyle(
      localTitle,
      markdownMenu.selectionStart,
      markdownMenu.selectionEnd,
      style
    );
    commitMarkdownEdit(result.value, result.selectionStart, result.selectionEnd);
  };

  const applyMarkdownTextColorFromMenu = (color: MarkdownTextColor | null) => {
    if (!markdownMenu) return;
    const result = applyMarkdownTextColor(
      localTitle,
      markdownMenu.selectionStart,
      markdownMenu.selectionEnd,
      color
    );
    commitMarkdownEdit(result.value, result.selectionStart, result.selectionEnd);
  };

  const toggleMarkdownColorPalette = () => {
    setMarkdownMenu(current => {
      if (!current) return current;
      const colorPaletteOpen = !current.colorPaletteOpen;
      return {
        ...current,
        colorPaletteOpen,
        y: colorPaletteOpen
          ? Math.min(current.y, window.innerHeight - 92 - 12)
          : current.y
      };
    });
  };

  const openMarkdownContextMenu = (
    input: HTMLTextAreaElement,
    clientX: number,
    clientY: number,
    placement: "pointer" | "selection" = "pointer"
  ) => {
    const selectionStart = input.selectionStart ?? 0;
    const selectionEnd = input.selectionEnd ?? selectionStart;
    if (selectionStart === selectionEnd) return false;
    const menuWidth = 224;
    const menuHeight = 46;
    const requestedX = placement === "selection" ? clientX - menuWidth / 2 : clientX;
    const requestedY = placement === "selection" ? clientY - menuHeight - 10 : clientY;
    setMarkdownMenu({
      x: Math.max(12, Math.min(requestedX, window.innerWidth - menuWidth - 12)),
      y: Math.max(12, Math.min(requestedY, window.innerHeight - menuHeight - 12)),
      selectionStart,
      selectionEnd,
      colorPaletteOpen: false
    });
    return true;
  };

  const openNodeContextMenu = (clientX: number, clientY: number) => {
    const menuWidth = 220;
    const menuHeight = 184;
    setMarkdownMenu(null);
    setNodeContextMenu({
      x: Math.max(12, Math.min(clientX, window.innerWidth - menuWidth - 12)),
      y: Math.max(12, Math.min(clientY, window.innerHeight - menuHeight - 12))
    });
  };

  const rowClassName = [
    "nodeRow",
    selected ? "selected" : "",
    selectionPosition ? `selection-${selectionPosition}` : "",
    active ? "active" : "",
    node.done ? "completed" : "",
    node.collapsed && node.childIds.length > 0 ? "collapsedChildren" : "",
    dragging ? "dragging" : "",
    dropPlacement ? `drop-${dropPlacement}` : ""
  ]
    .filter(Boolean)
    .join(" ");
  const childCountLabel = getChildCountLabel(node.childIds.length);
  const hasComments = node.body.trim().length > 0;
  const renderTitleLiterally = isMarkdownThematicBreak(node.title);
  const openDatePicker = () => {
    const input = dateInputRef.current;
    if (!input) return;
    try {
      input.showPicker();
    } catch {
      input.focus();
    }
  };
  const commitDueDate = (dueDate: string | null) => {
    onPatchLocal({ dueDate });
    onCommit({ dueDate });
  };
  useEffect(() => {
    const input = titleInputRef.current;
    if (!input) return;
    resizeTitleInput(input);
  }, [localTitle]);

  return (
    <div
      className={rowClassName}
      data-node-id={node.id}
      data-selected={selected ? "true" : "false"}
      data-active={active ? "true" : "false"}
      style={{ "--depth": depth } as CSSProperties}
      onPointerDown={onSelectionStart}
      onContextMenu={event => {
        if (node.id.startsWith("temp-")) return;
        event.preventDefault();
        event.stopPropagation();
        onContextSelect();
        openNodeContextMenu(event.clientX, event.clientY);
      }}
      onClick={event => {
        const target = event.target as HTMLElement;
        if (
          target.closest(".nodeTitle") ||
          target.closest(".nodeMenuButton") ||
          target.closest(".disclosureButton") ||
          target.closest(".dragHandle") ||
          target.closest(".iconButton.danger") ||
          target.closest(".nodeDateControl") ||
          target.closest(".nodeTags")
        ) return;
        const input = titleInputRef.current;
        if (input && onMouseSelect(event)) {
          input.focus({ preventScroll: true });
        }
      }}
    >
      {depth > 0 && (
        <span className="nodeTreeGuides" aria-hidden="true">
          {Array.from({ length: depth }, (_, index) => {
            const guideDepth = index + 1;
            return <i key={guideDepth} style={{ "--guide-depth": guideDepth } as CSSProperties} />;
          })}
        </span>
      )}
      <button
        className="iconButton nodeMenuButton"
        type="button"
        title="More outline actions"
        aria-label={`More actions for ${node.title || "Untitled"}`}
        aria-haspopup="menu"
        aria-expanded={Boolean(nodeContextMenu)}
        disabled={node.id.startsWith("temp-")}
        onPointerDown={event => event.stopPropagation()}
        onClick={event => {
          event.preventDefault();
          event.stopPropagation();
          onContextSelect();
          const bounds = event.currentTarget.getBoundingClientRect();
          openNodeContextMenu(bounds.left, bounds.bottom + 4);
        }}
      >
        <Ellipsis size={16} />
      </button>
      <button
        className="iconButton disclosureButton"
        type="button"
        title={node.collapsed ? "Expand" : "Collapse"}
        disabled={node.childIds.length === 0}
        onClick={() => onToggle({ collapsed: !node.collapsed })}
      >
        {node.childIds.length > 0 ? (
          <span className={node.collapsed ? "disclosureTriangle right" : "disclosureTriangle down"} />
        ) : null}
      </button>
      <button
        className={[
          "dragHandle",
          node.done && "done",
          node.collapsed && node.childIds.length > 0 && "collapsed",
          hasComments && "hasComments"
        ].filter(Boolean).join(" ")}
        type="button"
        title={canDrag ? "Drag to move an outline or drop onto a workspace" : "Move unavailable"}
        aria-label={hasComments ? "Move node; has comments" : "Move node"}
        disabled={!canDrag}
        onPointerDown={onMoveStart}
      >
        <span className="nodeDot" />
      </button>
      <div className="nodeTitleCell">
        <textarea
          ref={element => {
            titleInputRef.current = element;
            registerInput(element);
          }}
          className="nodeTitle"
          {...inlineTags.inputProps}
          value={localTitle}
          placeholder="Untitled"
          rows={1}
          onFocus={event => {
            inlineTags.refresh(event.currentTarget);
            if (!node.id.startsWith("temp-")) beginNodeEdit(node.id, "title", node.title);
            onFocusSelect();
          }}
          onClick={event => {
            const input = event.currentTarget;
            if (input.selectionStart !== input.selectionEnd) return;
            const measure = titleMeasureRef.current;
            if (!measure) return;
            const selectionStart = getPreviewSelectionStart(
              measure,
              event.clientX,
              event.clientY,
              localTitle
            );
            input.setSelectionRange(selectionStart, selectionStart);
          }}
          onChange={event => {
            const value = event.target.value;
            setLocalTitle(value);
            resizeTitleInput(event.currentTarget);
            syncTitleDebounced(value);
            inlineTags.refresh(event.currentTarget);
          }}
          onSelect={event => inlineTags.refresh(event.currentTarget)}
          onCompositionStart={() => {
            inlineTags.composing.current = true;
            inlineTags.close();
            onCompositionChange(true);
          }}
          onCompositionEnd={event => {
            const value = event.currentTarget.value;
            setLocalTitle(value);
            syncTitleDebounced(value);
            onCompositionChange(false);
            inlineTags.composing.current = false;
            inlineTags.refresh(event.currentTarget);
          }}
          onPaste={event => {
            const input = event.currentTarget;
            const result = applyPastedMarkdownLink(
              localTitle,
              input.selectionStart ?? localTitle.length,
              input.selectionEnd ?? input.selectionStart ?? localTitle.length,
              event.clipboardData.getData("text/plain")
            );
            if (!result) {
              setMarkdownMenu(null);
              return;
            }
            event.preventDefault();
            commitMarkdownEdit(result.value, result.selectionStart, result.selectionEnd);
          }}
          onBlur={event => {
            inlineTags.close();
            inlineTags.composing.current = false;
            onCompositionChange(false);
            if (syncTimerRef.current) { clearTimeout(syncTimerRef.current); syncTimerRef.current = null; }
            // A stale editor that was only focused must never write its old value.
            if (node.id.startsWith("temp-")) flushTitle(event.target.value);
            else void endNodeEdit(node.id, "title").catch(() => {});
            onBlurFocus();
          }}
          onPointerDown={event => {
            if (event.button === 0) {
              const measure = titleMeasureRef.current;
              if (!measure) return;
              textSelectionPointerRef.current = {
                pointerId: event.pointerId,
                startX: event.clientX,
                startY: event.clientY,
                startOffset: getPreviewSelectionStart(measure, event.clientX, event.clientY, localTitle)
              };
              return;
            }
            if (event.button === 2 && openMarkdownContextMenu(event.currentTarget, event.clientX, event.clientY)) {
              event.preventDefault();
              event.stopPropagation();
            }
          }}
          onPointerUp={event => {
            if (event.button !== 0) return;
            const input = event.currentTarget;
            const pointer = textSelectionPointerRef.current;
            textSelectionPointerRef.current = null;
            const measure = titleMeasureRef.current;
            if (
              pointer?.pointerId === event.pointerId &&
              measure &&
              Math.hypot(event.clientX - pointer.startX, event.clientY - pointer.startY) >= 4
            ) {
              const endOffset = getPreviewSelectionStart(measure, event.clientX, event.clientY, localTitle);
              input.setSelectionRange(
                Math.min(pointer.startOffset, endOffset),
                Math.max(pointer.startOffset, endOffset)
              );
            }
            if (input.selectionStart === input.selectionEnd) return;
            event.stopPropagation();
            openMarkdownContextMenu(input, event.clientX, event.clientY, "selection");
          }}
          onContextMenu={event => {
            if (!openMarkdownContextMenu(event.currentTarget, event.clientX, event.clientY)) return;
            event.preventDefault();
            event.stopPropagation();
          }}
          onKeyUp={event => {
            const input = event.currentTarget;
            if (input.selectionStart === input.selectionEnd) return;
            const key = event.key.toLowerCase();
            if (!event.shiftKey && !((event.metaKey || event.ctrlKey) && key === "a")) return;
            const bounds = input.getBoundingClientRect();
            openMarkdownContextMenu(input, bounds.left + bounds.width / 2, bounds.top, "selection");
          }}
          onKeyDown={event => {
            if (shouldIgnoreTextInputKeyDown(event)) return;
            if (inlineTags.onKeyDown(event)) return;
            if (handleMarkdownShortcut(event, localTitle, patch => {
              if (patch.title !== undefined) {
                setLocalTitle(patch.title);
                flushTitle(patch.title);
              }
            })) return;
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              const input = event.currentTarget;
              const action = getNodeEnterAction(localTitle, input.selectionStart, input.selectionEnd);
              if (action.type === "insert-before") {
                flushTitle(localTitle);
                onCreateBefore(localTitle);
                return;
              }
              const { currentTitle, nextTitle } = action;
              // Batch all state updates: flush current title + create new node
              setLocalTitle(currentTitle);
              flushTitle(currentTitle);
              onCreateAfter(nextTitle, currentTitle);
            } else if (event.key === "Tab") {
              event.preventDefault();
              if (event.shiftKey) onOutdent();
              else onIndent();
            } else if (event.key === "ArrowUp") {
              if (!event.shiftKey && !event.altKey && !event.metaKey && !event.ctrlKey && isTitleCaretAtEdge(event.currentTarget, titleMeasureRef.current, "start")) {
                event.preventDefault();
                onFocusPrevious();
              }
            } else if (event.key === "ArrowDown") {
              if (!event.shiftKey && !event.altKey && !event.metaKey && !event.ctrlKey && isTitleCaretAtEdge(event.currentTarget, titleMeasureRef.current, "end")) {
                event.preventDefault();
                onFocusNext();
              }
            } else if (event.key === "Backspace" && !localTitle) {
              event.preventDefault();
              onDelete();
            }
          }}
        />
        <div
          className="nodeTitlePreview"
          onPointerDown={event => {
            if (
              event.button !== 0 ||
              event.shiftKey ||
              event.metaKey ||
              event.ctrlKey ||
              (event.target as HTMLElement).closest(".nodeTitleLink")
            ) return;
            event.preventDefault();
            const input = titleInputRef.current;
            if (!input) return;
            const selectionStart = getPreviewSelectionStart(
              event.currentTarget,
              event.clientX,
              event.clientY,
              node.title
            );
            onFocusSelect();
            input.focus({ preventScroll: true });
            input.setSelectionRange(selectionStart, selectionStart);
            window.requestAnimationFrame(() => {
              if (document.activeElement !== input) return;
              input.setSelectionRange(selectionStart, selectionStart);
            });
          }}
          onClick={event => {
            event.stopPropagation();
            if ((event.target as HTMLElement).closest(".nodeTitleLink")) return
            if (!event.shiftKey && !event.metaKey && !event.ctrlKey) return;
            if (!onMouseSelect(event)) return;
          }}
        >
          {node.title.trim() ? (
            renderTitleLiterally ? (
              <span>{node.title}</span>
            ) : (
              <ReactMarkdown
                allowedElements={["p", "strong", "em", "del", "code", "a", "br", "mark", "span"]}
                rehypePlugins={[rehypeInlineFormatting, [rehypeSanitize, markdownSanitizeSchema]]}
                remarkPlugins={[remarkGfm, remarkLiteralHtml]}
                unwrapDisallowed
                components={{
                  a: ({ children, href }) => {
                    const normalizedHref = href ? normalizeLinkHref(href) : undefined
                    return (
                      <a
                        className="nodeTitleLink"
                        href={normalizedHref}
                        onPointerDown={event => event.stopPropagation()}
                        onClick={event => {
                          event.preventDefault()
                          event.stopPropagation()
                          if (normalizedHref) window.open(normalizedHref, "_blank", "noopener,noreferrer")
                        }}
                      >
                        {children}
                      </a>
                    )
                  },
                  p: ({ children }) => <span>{children}</span>
                }}
              >
                {node.title}
              </ReactMarkdown>
            )
          ) : (
            <span className="nodeTitlePlaceholder">Untitled</span>
          )}
        </div>
        <div ref={titleMeasureRef} className="nodeTitleMeasure" aria-hidden="true">
          {localTitle}
        </div>
        {inlineTags.menu}
      </div>
      <div className="nodeDateControl">
        <input
          ref={dateInputRef}
          className="nodeDatePicker"
          type="date"
          value={node.dueDate ?? ""}
          aria-label={`Date for ${node.title || "Untitled"}`}
          onChange={event => commitDueDate(event.target.value || null)}
        />
        {node.dueDate ? (
          <>
            <button
              className="nodeDateChip"
              type="button"
              title="Change date"
              onClick={event => {
                event.stopPropagation();
                openDatePicker();
              }}
            >
              {formatNodeDate(node.dueDate)}
            </button>
            <button
              className="nodeDateClearButton"
              type="button"
              title="Remove date"
              aria-label="Remove date"
              onClick={event => {
                event.stopPropagation();
                commitDueDate(null);
              }}
            >
              <CalendarX2 size={14} />
            </button>
          </>
        ) : (
          <button
            className="nodeDateAddButton"
            type="button"
            title="Add date"
            aria-label="Add date"
            onClick={event => {
              event.stopPropagation();
              openDatePicker();
            }}
          >
            <CalendarPlus size={17} />
          </button>
        )}
      </div>
      {childCountLabel ? (
        <span className="nodeChildCount">{childCountLabel}</span>
      ) : (
        <span className="nodeChildCount nodeChildCountPlaceholder" aria-hidden="true">0</span>
      )}
      <div className="nodeTags">
        {(node.tags || []).map(tag => (
          <span className="nodeTagChip" key={tag.id}>
            <button type="button" onClick={() => onTagClick(tag)}>{tag.name}</button>
            <button
              className="nodeTagRemove"
              type="button"
              aria-label={`移除当前节点的标签 ${tag.name}`}
              title="解除当前节点关联，保留标签"
              disabled={tag.id.startsWith("temp-")}
              onClick={event => {
                event.stopPropagation();
                onTagRemove(tag);
              }}
            >
              <X size={12} />
            </button>
          </span>
        ))}
      </div>
      <button className="iconButton danger" type="button" title="Delete" onClick={() => onDelete()}>
        <Trash2 size={15} />
      </button>
      {markdownMenu && createPortal(
        <div
          ref={markdownMenuRef}
          className="markdownContextMenu"
          role="dialog"
          aria-label="Format selected text"
          style={{ left: markdownMenu.x, top: markdownMenu.y }}
          onPointerDown={event => event.stopPropagation()}
          onClick={event => event.stopPropagation()}
          onContextMenu={event => {
            event.preventDefault()
            event.stopPropagation()
          }}
        >
          <div className="markdownContextMenuHeader">
            <span>Format selection</span>
            <small>Markdown</small>
          </div>
          <div className="markdownFormatButtons">
            <button type="button" aria-label="Bold" onPointerDown={event => event.preventDefault()} onClick={() => applyMarkdownStyleFromMenu("bold")}>
              <Bold size={16} />
              <span>Bold</span>
            </button>
            <button type="button" aria-label="Italic" onPointerDown={event => event.preventDefault()} onClick={() => applyMarkdownStyleFromMenu("italic")}>
              <Italic size={16} />
              <span>Italic</span>
            </button>
            <button type="button" aria-label="Strike" onPointerDown={event => event.preventDefault()} onClick={() => applyMarkdownStyleFromMenu("strike")}>
              <Strikethrough size={16} />
              <span>Strike</span>
            </button>
            <button type="button" aria-label="Inline code" onPointerDown={event => event.preventDefault()} onClick={() => applyMarkdownStyleFromMenu("code")}>
              <Code2 size={16} />
              <span>Code</span>
            </button>
            <button className="markdownHighlightButton" type="button" aria-label="Highlight" onPointerDown={event => event.preventDefault()} onClick={() => applyMarkdownStyleFromMenu("highlight")}>
              <Highlighter size={16} />
              <span>Highlight</span>
            </button>
            <button
              className="markdownColorButton"
              type="button"
              aria-label="Text color"
              aria-expanded={markdownMenu.colorPaletteOpen}
              onPointerDown={event => event.preventDefault()}
              onClick={toggleMarkdownColorPalette}
            >
              <Palette size={16} />
              <span>Text color</span>
            </button>
          </div>
          {markdownMenu.colorPaletteOpen && (
            <div className="markdownColorPalette" role="group" aria-label="Text colors">
              <button
                className="markdownColorSwatch markdownColorSwatch-default"
                type="button"
                aria-label="Default color"
                onPointerDown={event => event.preventDefault()}
                onClick={() => applyMarkdownTextColorFromMenu(null)}
              >
                <i>A</i>
                <span>Default</span>
              </button>
              {MARKDOWN_TEXT_COLORS.map(color => (
                <button
                  className={`markdownColorSwatch markdownColorSwatch-${color.id}`}
                  type="button"
                  aria-label={`${color.label} text`}
                  key={color.id}
                  onPointerDown={event => event.preventDefault()}
                  onClick={() => applyMarkdownTextColorFromMenu(color.id)}
                >
                  <i />
                  <span>{color.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>,
        document.body
      )}
      {nodeContextMenu && createPortal(
        <div
          ref={nodeContextMenuRef}
          className="nodeContextMenu"
          role="menu"
          aria-label="Outline actions"
          style={{ left: nodeContextMenu.x, top: nodeContextMenu.y }}
          onPointerDown={event => event.stopPropagation()}
          onClick={event => event.stopPropagation()}
          onContextMenu={event => {
            event.preventDefault();
            event.stopPropagation();
          }}
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              const complete = async () => {
                flushTitle(localTitle);
                if (!node.id.startsWith("temp-")) await flushNodeDraft(node.id, "title");
                setNodeContextMenu(null);
                await onComplete();
              };
              void complete().catch(onTagError);
            }}
          >
            <CircleCheck size={16} />
            <span>Complete</span>
          </button>
          <button
            type="button"
            role="menuitem"
            disabled={!/[\r\n]/.test(localTitle)}
            onClick={() => {
              const split = async () => {
                flushTitle(localTitle);
                if (!node.id.startsWith("temp-")) await flushNodeDraft(node.id, "title");
                setNodeContextMenu(null);
                await onSplitLines(localTitle);
              };
              void split().catch(onTagError);
            }}
          >
            <ListPlus size={16} />
            <span>Split by line breaks</span>
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              flushTitle(localTitle);
              if (!node.id.startsWith("temp-")) void flushNodeDraft(node.id, "title").catch(() => {});
              setNodeContextMenu(null);
              onConvertToWorkspace(localTitle);
            }}
          >
            <FolderTree size={16} />
            <span>Convert to workspace</span>
          </button>
        </div>,
        document.body
      )}
    </div>
  );
}

function TagResultRow({
  result,
  selected,
  onOpen,
  onTagClick
}: {
  result: TaggedNodeResult;
  selected: boolean;
  onOpen: () => void;
  onTagClick: (tag: Tag) => void;
}) {
  return (
    <div className={selected ? "tagResultRow selected" : "tagResultRow"}>
      <button className="tagResultMain" type="button" onClick={onOpen}>
        <span className="tagResultTitle">{result.node.title || "Untitled"}</span>
        <span className="tagResultWorkspace">{result.workspace.name}</span>
        {result.node.body && <span className="tagResultBody">{result.node.body}</span>}
      </button>
      <div className="nodeTags">
        {(result.tags || []).map(tag => (
          <button type="button" key={tag.id} onClick={() => onTagClick(tag)}>
            {tag.name}
          </button>
        ))}
      </div>
    </div>
  );
}

function RecycleBinRow({
  entry,
  restoring,
  disabled,
  onRestore
}: {
  entry: RecycleBinEntry;
  restoring: boolean;
  disabled: boolean;
  onRestore: () => void;
}) {
  const nodesById = new Map([entry.node, ...entry.descendants].map(node => [node.id, node]));
  const depthFor = (node: RecycleBinEntry["node"]) => {
    let depth = 0;
    let parentId = node.parentId;
    while (parentId && parentId !== entry.node.id) {
      const parent = nodesById.get(parentId);
      if (!parent) break;
      depth += 1;
      parentId = parent.parentId;
    }
    return depth;
  };

  return (
    <article className="recycleBinRow">
      <div className="recycleBinMain">
        <div className="recycleBinHeading">
          <span className="recycleBinTitle">{entry.node.title || "Untitled"}</span>
          <span className="recycleBinWorkspace">{entry.workspace.name}</span>
        </div>
        {entry.node.body && <p className="recycleBinBody">{entry.node.body}</p>}
        <div className="recycleBinMeta">
          <span>{new Date(entry.completedAt).toLocaleString()}</span>
          <span>{entry.descendants.length === 0 ? "No nested outlines" : `${entry.descendants.length} nested outlines`}</span>
        </div>
        {entry.descendants.length > 0 && (
          <details className="recycleBinChildren">
            <summary>Show nested outlines</summary>
            <div>
              {entry.descendants.map(node => (
                <div className="recycleBinChild" key={node.id} style={{ "--recycle-depth": depthFor(node) } as CSSProperties}>
                  <span>{node.title || "Untitled"}</span>
                  {node.body && <small>{node.body}</small>}
                </div>
              ))}
            </div>
          </details>
        )}
      </div>
      <button className="recycleBinRestore" type="button" disabled={disabled || restoring} onClick={onRestore}>
        <Undo2 size={15} />
        <span>{restoring ? "Restoring…" : "Restore"}</span>
      </button>
    </article>
  );
}

function SystemTagGroupRow({ group, collapsed, onToggle, onRename, onDelete }: {
  group: TaggedNodeGroup;
  collapsed: boolean;
  onToggle: () => void;
  onRename: (name: string) => Promise<void>;
  onDelete: () => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(group.name);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const act = async (action: () => Promise<void>) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError("");
    try { await action(); setEditing(false); }
    catch (error) { setError(error instanceof Error ? error.message : "Unable to update tag."); }
    finally { busyRef.current = false; setBusy(false); }
  };
  const cancel = () => { setEditing(false); setName(group.name); setError(""); };
  return (
    <div className="systemTagGroupRow" style={{ "--system-tag-color": group.color } as CSSProperties}>
      {editing ? <>
        <input className="systemTagRenameInput" autoFocus value={name} disabled={busy}
          aria-label={`Rename tag ${group.name}`}
          onChange={event => setName(event.target.value)}
          onKeyDown={event => {
            if (shouldIgnoreTextInputKeyDown(event)) return;
            if (event.key === "Enter") { event.preventDefault(); void act(() => onRename(name)); }
            if (event.key === "Escape") { event.preventDefault(); cancel(); }
          }} />
        <button className="systemTagAction" type="button" title="Save tag name" aria-label="Save tag name" disabled={busy || !name.trim()}
          onClick={() => void act(() => onRename(name))}><Check size={15} /></button>
        <button className="systemTagAction" type="button" title="Cancel rename" aria-label="Cancel rename" disabled={busy} onClick={cancel}><X size={15} /></button>
      </> : <>
        <button className="systemTagGroupToggle" type="button" aria-expanded={!collapsed} onClick={onToggle}>
          <span className="systemTagDisclosure" aria-hidden="true">{collapsed ? <ChevronRight size={15} /> : <ChevronDown size={15} />}</span>
          <span className="systemTagColor" aria-hidden="true" />
          <strong>#{group.name}</strong>
          <span className="systemTagCount">{group.results.length}</span>
        </button>
        <button className="systemTagAction" type="button" title="Rename tag" aria-label={`Rename tag ${group.name}`} disabled={busy}
          onClick={() => { setName(group.name); setEditing(true); setError(""); }}><Pencil size={14} /></button>
        <button className="systemTagAction systemTagDelete" type="button" title="Delete tag" aria-label={`Delete tag ${group.name}`} disabled={busy}
          onClick={() => void act(onDelete)}><Trash2 size={14} /></button>
      </>}
      {error && <span className="systemTagError" role="alert">{error}</span>}
    </div>
  );
}

function SystemTaggedNodeRow({
  color,
  result,
  onOpen
}: {
  color: string;
  result: TaggedNodeResult;
  onOpen: () => void;
}) {
  const path = result.path.length > 0
    ? result.path
    : [{ id: result.node.id, title: result.node.title, position: result.node.position }];
  const target = path[path.length - 1];
  const ancestorTitles = path.slice(0, -1).map(segment => segment.title || "Untitled");
  const targetTitle = target.title || "Untitled";
  const breadcrumbTitle = `${result.workspace.name} · ${[...ancestorTitles, targetTitle].join(" / ")}`;

  return (
    <button
      className={result.node.done ? "systemTaggedNodeRow completed" : "systemTaggedNodeRow"}
      type="button"
      onClick={onOpen}
      style={{ "--system-tag-color": color } as CSSProperties}
      title={breadcrumbTitle}
    >
      <span className="systemTagBranch" aria-hidden="true">
        <span />
      </span>
      <span className="systemTaggedNodeCopy">
        <span className="systemTaggedNodeBreadcrumb" aria-label={breadcrumbTitle}>
          {ancestorTitles.length > 0 && (
            <>
              <span className="systemTaggedNodeAncestors">{ancestorTitles.join(" / ")}</span>
              <span className="systemTaggedNodeSeparator" aria-hidden="true">/</span>
            </>
          )}
          <strong>{targetTitle}</strong>
        </span>
        {result.node.body && <small>{result.node.body}</small>}
      </span>
      <span className="systemTaggedNodeWorkspace">
        {result.workspace.name}
        <ChevronRight size={14} aria-hidden="true" />
      </span>
    </button>
  );
}

export function buildSystemTagRows(
  groups: TaggedNodeGroup[],
  collapsedNames: ReadonlySet<string>,
  query: string
): SystemTagRow[] {
  const normalizedQuery = query.trim().toLowerCase();
  const rows: SystemTagRow[] = [];

  for (const group of groups) {
    const groupMatches = group.name.toLowerCase().includes(normalizedQuery);
    const matchingResults = !normalizedQuery || groupMatches
      ? group.results
      : group.results.filter(result =>
          `${result.node.title}\n${result.node.body}\n${result.workspace.name}\n${result.path.map(segment => segment.title).join("\n")}`
            .toLowerCase()
            .includes(normalizedQuery)
        );
    if (normalizedQuery && !groupMatches && matchingResults.length === 0) continue;

    rows.push({ kind: "tag", group });
    if (!normalizedQuery && collapsedNames.has(group.name)) continue;
    for (const result of matchingResults) {
      rows.push({ kind: "node", groupName: group.name, color: group.color, result });
    }
  }

  return rows;
}

function systemTagRowKey(row: SystemTagRow | undefined, index: number): string {
  if (!row) return `system-tag-row-${index}`;
  return row.kind === "tag" ? `system-tag:${row.group.name}` : `system-node:${row.groupName}:${row.result.node.id}`;
}

function getDropPlacement(element: HTMLElement, clientY: number): DropPlacement {
  const bounds = element.getBoundingClientRect();
  const offset = clientY - bounds.top;
  if (offset < bounds.height * 0.28) return "before";
  if (offset > bounds.height * 0.72) return "after";
  return "inside";
}

function getWorkspaceDropPlacement(element: HTMLElement, clientY: number): WorkspaceDropPlacement {
  const bounds = element.getBoundingClientRect();
  const offset = clientY - bounds.top;
  if (offset < bounds.height * 0.28) return "before";
  if (offset > bounds.height * 0.72) return "after";
  return "inside";
}

function reorderWorkspaces(
  workspaces: Workspace[],
  workspaceId: string,
  folderId: string | null,
  parentWorkspaceId: string | null,
  position: number
): Workspace[] {
  const moving = workspaces.find(workspace => workspace.id === workspaceId);
  if (!moving) return workspaces;

  const withoutMoving = workspaces.filter(workspace => workspace.id !== workspaceId);
  const siblings = withoutMoving.filter(
    workspace => workspace.folderId === folderId && workspace.parentWorkspaceId === parentWorkspaceId
  );
  const nextPosition = Math.max(0, Math.min(position, siblings.length));
  const normalized = withoutMoving.map(workspace => {
    if (
      workspace.folderId === moving.folderId &&
      workspace.parentWorkspaceId === moving.parentWorkspaceId &&
      workspace.position > moving.position
    ) {
      return { ...workspace, position: workspace.position - 1 };
    }
    if (
      workspace.folderId === folderId &&
      workspace.parentWorkspaceId === parentWorkspaceId &&
      workspace.position >= nextPosition
    ) {
      return { ...workspace, position: workspace.position + 1 };
    }
    return workspace;
  });

  normalized.push({ ...moving, folderId, parentWorkspaceId, position: nextPosition });
  return normalized.sort((a, b) => {
    const folderCompare = (a.folderId ?? "").localeCompare(b.folderId ?? "");
    if (folderCompare !== 0) return folderCompare;
    const parentCompare = (a.parentWorkspaceId ?? "").localeCompare(b.parentWorkspaceId ?? "");
    if (parentCompare !== 0) return parentCompare;
    if (a.position !== b.position) return a.position - b.position;
    return a.createdAt.localeCompare(b.createdAt);
  });
}

function isWorkspaceDescendant(workspaces: Workspace[], workspaceId: string, ancestorId: string): boolean {
  let current = workspaces.find(workspace => workspace.id === workspaceId);
  const visited = new Set<string>();
  while (current?.parentWorkspaceId && !visited.has(current.id)) {
    visited.add(current.id);
    if (current.parentWorkspaceId === ancestorId) return true;
    current = workspaces.find(workspace => workspace.id === current?.parentWorkspaceId);
  }
  return false;
}

function handleMarkdownShortcut(
  event: KeyboardEvent<HTMLTextAreaElement>,
  title: string,
  onPatchLocal: (patch: Partial<FlatNodeData>) => void
): boolean {
  if (!event.ctrlKey && !event.metaKey) return false;

  const key = event.key.toLowerCase();
  if (key === "k" && !event.shiftKey) {
    insertMarkdownLinkFromClipboard(event, title, onPatchLocal);
    return true;
  }

  const shortcut =
    key === "b" && !event.shiftKey
      ? { style: "bold" as const, placeholder: "bold" }
      : key === "i" && !event.shiftKey
        ? { style: "italic" as const, placeholder: "italic" }
        : key === "e" && !event.shiftKey
          ? { style: "code" as const, placeholder: "code" }
          : key === "x" && (event.altKey || event.shiftKey)
            ? { style: "strike" as const, placeholder: "strike" }
            : key === "h" && event.shiftKey
              ? { style: "highlight" as const, placeholder: "highlight" }
              : null;

  if (!shortcut) return false;

  event.preventDefault();
  const input = event.currentTarget;
  const start = input.selectionStart ?? title.length;
  const end = input.selectionEnd ?? start;
  const result = applyMarkdownStyle(title, start, end, shortcut.style, shortcut.placeholder);

  onPatchLocal({ title: result.value });
  window.setTimeout(() => {
    input.setSelectionRange(result.selectionStart, result.selectionEnd);
  }, 0);
  return true;
}

const markdownStyleMarkers: Record<MarkdownStyle, { before: string; after: string }> = {
  bold: { before: "**", after: "**" },
  italic: { before: "*", after: "*" },
  strike: { before: "~~", after: "~~" },
  code: { before: "`", after: "`" },
  highlight: { before: "==", after: "==" }
};

export function applyMarkdownStyle(
  value: string,
  selectionStart: number,
  selectionEnd: number,
  style: MarkdownStyle,
  placeholder = "text"
) {
  const start = Math.max(0, Math.min(selectionStart, value.length));
  const end = Math.max(start, Math.min(selectionEnd, value.length));
  const { before, after } = markdownStyleMarkers[style];
  const selected = value.slice(start, end) || placeholder;

  if (
    start >= before.length &&
    value.slice(start - before.length, start) === before &&
    value.slice(end, end + after.length) === after
  ) {
    return {
      value: `${value.slice(0, start - before.length)}${selected}${value.slice(end + after.length)}`,
      selectionStart: start - before.length,
      selectionEnd: end - before.length
    };
  }

  if (selected.startsWith(before) && selected.endsWith(after) && selected.length >= before.length + after.length) {
    const unwrapped = selected.slice(before.length, selected.length - after.length);
    return {
      value: `${value.slice(0, start)}${unwrapped}${value.slice(end)}`,
      selectionStart: start,
      selectionEnd: start + unwrapped.length
    };
  }

  return {
    value: `${value.slice(0, start)}${before}${selected}${after}${value.slice(end)}`,
    selectionStart: start + before.length,
    selectionEnd: start + before.length + selected.length
  };
}

function parseWholeMarkdownTextColor(value: string): { color: MarkdownTextColor; text: string } | null {
  const match = /^\{\{color:([a-z]+)\}\}([\s\S]*)\{\{\/color\}\}$/.exec(value);
  if (!match || !markdownTextColorIds.has(match[1])) return null;
  return { color: match[1] as MarkdownTextColor, text: match[2] };
}

export function applyMarkdownTextColor(
  value: string,
  selectionStart: number,
  selectionEnd: number,
  color: MarkdownTextColor | null
) {
  const start = Math.max(0, Math.min(selectionStart, value.length));
  const end = Math.max(start, Math.min(selectionEnd, value.length));
  const selected = value.slice(start, end);
  const endMarker = "{{/color}}";
  const prefixMatch = /\{\{color:([a-z]+)\}\}$/.exec(value.slice(0, start));
  const hasSurroundingColor = Boolean(
    prefixMatch &&
    markdownTextColorIds.has(prefixMatch[1]) &&
    value.slice(end, end + endMarker.length) === endMarker
  );

  if (prefixMatch && hasSurroundingColor) {
    const wrapperStart = start - prefixMatch[0].length;
    const replacement = color ? `{{color:${color}}}${selected}${endMarker}` : selected;
    const contentOffset = color ? `{{color:${color}}}`.length : 0;
    return {
      value: `${value.slice(0, wrapperStart)}${replacement}${value.slice(end + endMarker.length)}`,
      selectionStart: wrapperStart + contentOffset,
      selectionEnd: wrapperStart + contentOffset + selected.length
    };
  }

  const existingColor = parseWholeMarkdownTextColor(selected);
  if (existingColor) {
    const replacement = color ? `{{color:${color}}}${existingColor.text}${endMarker}` : existingColor.text;
    const contentOffset = color ? `{{color:${color}}}`.length : 0;
    return {
      value: `${value.slice(0, start)}${replacement}${value.slice(end)}`,
      selectionStart: start + contentOffset,
      selectionEnd: start + contentOffset + existingColor.text.length
    };
  }

  if (!color || !selected) {
    return { value, selectionStart: start, selectionEnd: end };
  }

  const before = `{{color:${color}}}`;
  return {
    value: `${value.slice(0, start)}${before}${selected}${endMarker}${value.slice(end)}`,
    selectionStart: start + before.length,
    selectionEnd: start + before.length + selected.length
  };
}

export function applyMarkdownLink(value: string, selectionStart: number, selectionEnd: number, href: string) {
  const start = Math.max(0, Math.min(selectionStart, value.length));
  const end = Math.max(start, Math.min(selectionEnd, value.length));
  const selected = value.slice(start, end) || "link";
  const existingLink = parseWholeMarkdownLink(selected);
  const label = existingLink?.label ?? selected;
  const normalizedHref = parseWholeMarkdownLink(href.trim())?.href.trim() || href.trim();
  const before = "[";
  const after = `](${normalizedHref})`;
  return {
    value: `${value.slice(0, start)}${before}${label}${after}${value.slice(end)}`,
    selectionStart: start + before.length,
    selectionEnd: start + before.length + label.length
  };
}

function parseWholeMarkdownLink(value: string): { label: string; href: string } | null {
  if (!value.startsWith("[") || !value.endsWith(")")) return null;

  let bracketDepth = 1;
  let closingBracket = -1;
  for (let index = 1; index < value.length; index += 1) {
    if (value[index] === "\\") {
      index += 1;
      continue;
    }
    if (value[index] === "[") bracketDepth += 1;
    else if (value[index] === "]") {
      bracketDepth -= 1;
      if (bracketDepth === 0) {
        closingBracket = index;
        break;
      }
    }
  }

  if (closingBracket < 0 || value[closingBracket + 1] !== "(") return null;

  let parenthesisDepth = 1;
  for (let index = closingBracket + 2; index < value.length; index += 1) {
    if (value[index] === "\\") {
      index += 1;
      continue;
    }
    if (value[index] === "(") parenthesisDepth += 1;
    else if (value[index] === ")") {
      parenthesisDepth -= 1;
      if (parenthesisDepth === 0) {
        if (index !== value.length - 1) return null;
        const href = value.slice(closingBracket + 2, index);
        return href ? { label: value.slice(1, closingBracket), href } : null;
      }
    }
  }

  return null;
}

export function normalizeLinkHref(href: string): string {
  const trimmed = href.trim();
  if (/^(?:https?:|mailto:|tel:|#)/i.test(trimmed)) return trimmed;
  return `https://${trimmed.replace(/^\/\//, "")}`;
}

export function isPastedMarkdownLink(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed || /\s/.test(trimmed)) return false;
  if (/^(?:https?:\/\/|mailto:|tel:|\/\/)/i.test(trimmed)) return true;
  return /^(?:www\.)?[a-z0-9](?:[a-z0-9-]*\.)+[a-z]{2,}(?::\d+)?(?:[/?#].*)?$/i.test(trimmed);
}

export function applyPastedMarkdownLink(
  value: string,
  selectionStart: number,
  selectionEnd: number,
  clipboardText: string
) {
  const start = Math.max(0, Math.min(selectionStart, value.length));
  const end = Math.max(start, Math.min(selectionEnd, value.length));
  if (start === end || !isPastedMarkdownLink(clipboardText)) return null;
  return applyMarkdownLink(value, start, end, normalizeLinkHref(clipboardText));
}

async function insertMarkdownLinkFromClipboard(
  event: KeyboardEvent<HTMLTextAreaElement>,
  title: string,
  onPatchLocal: (patch: Partial<FlatNodeData>) => void
) {
  event.preventDefault();
  const input = event.currentTarget;
  const start = input.selectionStart ?? title.length;
  const end = input.selectionEnd ?? start;
  const clipboardText = await readClipboardText();
  const href = clipboardText || "url";
  const result = applyMarkdownLink(title, start, end, href);

  onPatchLocal({ title: result.value });
  window.setTimeout(() => {
    focusTitleInput(input);
    input.setSelectionRange(result.selectionStart, result.selectionEnd);
  }, 0);
}

export function splitMarkdownHighlights(value: string) {
  const parts: Array<{ value: string; highlighted: boolean }> = [];
  const pattern = /==([^=\n]+)==/g;
  let cursor = 0;
  for (const match of value.matchAll(pattern)) {
    const index = match.index ?? 0;
    if (index > cursor) parts.push({ value: value.slice(cursor, index), highlighted: false });
    parts.push({ value: match[1], highlighted: true });
    cursor = index + match[0].length;
  }
  if (cursor < value.length) parts.push({ value: value.slice(cursor), highlighted: false });
  return parts.length > 0 ? parts : [{ value, highlighted: false }];
}

export function splitMarkdownTextColors(value: string) {
  const parts: Array<{ value: string; color: MarkdownTextColor | null }> = [];
  const pattern = /\{\{color:(red|orange|yellow|green|blue|purple|gray)\}\}([\s\S]*?)\{\{\/color\}\}/g;
  let cursor = 0;
  for (const match of value.matchAll(pattern)) {
    const index = match.index ?? 0;
    if (index > cursor) parts.push({ value: value.slice(cursor, index), color: null });
    parts.push({ value: match[2], color: match[1] as MarkdownTextColor });
    cursor = index + match[0].length;
  }
  if (cursor < value.length) parts.push({ value: value.slice(cursor), color: null });
  return parts.length > 0 ? parts : [{ value, color: null }];
}

interface HastNode {
  type: string;
  value?: string;
  tagName?: string;
  properties?: Record<string, unknown>;
  children?: HastNode[];
}

interface MarkdownNode {
  type: string;
  value?: string;
  children?: MarkdownNode[];
}

export function remarkLiteralHtml() {
  return (tree: MarkdownNode) => {
    const visit = (node: MarkdownNode) => {
      if (node.type === "html") node.type = "text";
      node.children?.forEach(visit);
    };
    visit(tree);
  };
}

export function isMarkdownThematicBreak(value: string) {
  return /^[ ]{0,3}(?:(?:\*[\t ]*){3,}|(?:-[\t ]*){3,}|(?:_[\t ]*){3,})$/.test(value);
}

function toMarkdownInlineNodes(value: string): HastNode[] {
  const nodes: HastNode[] = [];
  let cursor = 0;

  while (cursor < value.length) {
    const colorPattern = /\{\{color:(red|orange|yellow|green|blue|purple|gray)\}\}/g;
    colorPattern.lastIndex = cursor;
    let colorMatch = colorPattern.exec(value);
    while (colorMatch && value.indexOf("{{/color}}", colorMatch.index + colorMatch[0].length) < 0) {
      colorMatch = colorPattern.exec(value);
    }

    const highlightStart = value.indexOf("==", cursor);
    const highlightEnd = highlightStart >= 0 ? value.indexOf("==", highlightStart + 2) : -1;
    const colorStart = colorMatch?.index ?? -1;
    const nextColorStart = colorStart >= 0 ? colorStart : Number.POSITIVE_INFINITY;
    const nextHighlightStart = highlightEnd >= 0 ? highlightStart : Number.POSITIVE_INFINITY;
    const nextStart = Math.min(nextColorStart, nextHighlightStart);

    if (!Number.isFinite(nextStart)) {
      nodes.push({ type: "text", value: value.slice(cursor) });
      break;
    }
    if (nextStart > cursor) nodes.push({ type: "text", value: value.slice(cursor, nextStart) });

    if (nextStart === nextColorStart && colorMatch) {
      const contentStart = colorMatch.index + colorMatch[0].length;
      const contentEnd = value.indexOf("{{/color}}", contentStart);
      nodes.push({
        type: "element",
        tagName: "span",
        properties: { className: [`markdownTextColor-${colorMatch[1]}`] },
        children: toMarkdownInlineNodes(value.slice(contentStart, contentEnd))
      });
      cursor = contentEnd + "{{/color}}".length;
      continue;
    }

    nodes.push({
      type: "element",
      tagName: "mark",
      properties: {},
      children: toMarkdownInlineNodes(value.slice(highlightStart + 2, highlightEnd))
    });
    cursor = highlightEnd + 2;
  }

  return nodes;
}

function rehypeInlineFormatting() {
  return (tree: HastNode) => {
    const visit = (node: HastNode) => {
      if (!node.children || node.tagName === "code" || node.tagName === "pre") return;
      const children: HastNode[] = [];
      for (const child of node.children) {
        if (
          child.type === "text" &&
          (child.value?.includes("==") || child.value?.includes("{{color:"))
        ) {
          children.push(...toMarkdownInlineNodes(child.value));
        } else {
          visit(child);
          children.push(child);
        }
      }
      node.children = children;
    };
    visit(tree);
  };
}

export function clampPanelWidth(value: number, min: number, max: number): number {
  return Math.round(Math.min(Math.max(value, min), max));
}

function readStoredPanelWidth(key: string, fallback: number, min: number, max: number): number {
  if (typeof window === "undefined") return fallback;
  try {
    const stored = Number(window.localStorage.getItem(key));
    return Number.isFinite(stored) && stored > 0 ? clampPanelWidth(stored, min, max) : fallback;
  } catch {
    return fallback;
  }
}

function storePanelWidth(key: string, value: number) {
  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    // Width persistence is optional when storage is unavailable.
  }
}

export function resolveStoredBoolean(value: string | null, fallback: boolean): boolean {
  if (value === "true") return true;
  if (value === "false") return false;
  return fallback;
}

export function resolveStoredIdSet(value: string | null): Set<string> {
  if (!value) return new Set();
  try {
    const parsed: unknown = JSON.parse(value);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((id): id is string => typeof id === "string"));
  } catch {
    return new Set();
  }
}

export function resolvePendingNodeTitle(
  pendingTitle: string | undefined,
  draftTitle: string | undefined,
  createdTitle: string | undefined
): string {
  return pendingTitle ?? draftTitle ?? createdTitle ?? "";
}

export function applyCachedNodeTitles(
  state: FlatTreeState,
  titles: ReadonlyMap<string, string>
): FlatTreeState {
  let next = state;
  for (const [id, title] of titles) {
    const node = next.nodes[id];
    if (!node || node.title === title) continue;
    if (next === state) next = { ...state, nodes: { ...state.nodes } };
    next.nodes[id] = { ...node, title };
  }
  return next;
}

export function addOptimisticNodeTag(state: FlatTreeState, nodeId: string, tag: Tag): FlatTreeState {
  const node = state.nodes[nodeId];
  if (!node || node.tags.some(current => current.id === tag.id || current.name === tag.name)) return state;
  return updateNode(state, nodeId, {
    tags: [...node.tags, tag].sort((left, right) => left.name.localeCompare(right.name))
  });
}

export function reconcileOptimisticNodeTag(
  state: FlatTreeState,
  nodeId: string,
  optimisticTagId: string,
  savedTag: Tag
): FlatTreeState {
  const node = state.nodes[nodeId];
  if (!node) return state;
  const tags = node.tags.filter(tag => tag.id !== optimisticTagId && tag.id !== savedTag.id && tag.name !== savedTag.name);
  return updateNode(state, nodeId, {
    tags: [...tags, savedTag].sort((left, right) => left.name.localeCompare(right.name))
  });
}

export function removeOptimisticNodeTag(state: FlatTreeState, nodeId: string, tagId: string): FlatTreeState {
  const node = state.nodes[nodeId];
  if (!node || !node.tags.some(tag => tag.id === tagId)) return state;
  return updateNode(state, nodeId, { tags: node.tags.filter(tag => tag.id !== tagId) });
}

export function upsertWorkspaceTag(tags: Tag[], savedTag: Tag): Tag[] {
  const next = tags.filter(tag => tag.id !== savedTag.id && tag.name !== savedTag.name);
  return [...next, savedTag].sort((left, right) => left.name.localeCompare(right.name));
}

function readStoredBoolean(key: string, fallback: boolean): boolean {
  if (typeof window === "undefined") return fallback;
  try {
    return resolveStoredBoolean(window.localStorage.getItem(key), fallback);
  } catch {
    return fallback;
  }
}

function storeBoolean(key: string, value: boolean) {
  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    // State persistence is optional when storage is unavailable.
  }
}

function readStoredIdSet(key: string): Set<string> {
  if (typeof window === "undefined") return new Set();
  try {
    return resolveStoredIdSet(window.localStorage.getItem(key));
  } catch {
    return new Set();
  }
}

function storeIdSet(key: string, values: ReadonlySet<string>) {
  try {
    window.localStorage.setItem(key, JSON.stringify([...values].sort()));
  } catch {
    // State persistence is optional when storage is unavailable.
  }
}

function resizeTitleInput(input: HTMLTextAreaElement) {
  const currentHeight = input.style.height;
  input.style.height = "auto";
  const nextHeight = `${input.scrollHeight}px`;
  input.style.height = currentHeight === nextHeight ? currentHeight : nextHeight;
}

function focusTitleInput(input?: HTMLTextAreaElement | null, selection?: TitleSelection) {
  if (!input) return;
  input.focus({ preventScroll: true });
  if (!selection) return;
  const range = resolveTitleSelection(input.value, selection.start, selection.end);
  input.setSelectionRange(range.start, range.end);
}

export function resolveTitleSelection(value: string, selectionStart: number, selectionEnd: number) {
  const start = Math.max(0, Math.min(selectionStart, value.length));
  const end = Math.max(start, Math.min(selectionEnd, value.length));
  return { start, end };
}

export function splitTitleAtSelection(title: string, selectionStart?: number | null) {
  const splitIndex = Math.max(0, Math.min(selectionStart ?? title.length, title.length));
  return {
    currentTitle: title.slice(0, splitIndex),
    nextTitle: title.slice(splitIndex)
  };
}

export type NodeEnterAction =
  | { type: "insert-before" }
  | { type: "split"; currentTitle: string; nextTitle: string };

export function getNodeEnterAction(
  title: string,
  selectionStart?: number | null,
  selectionEnd?: number | null
): NodeEnterAction {
  if (title.length > 0 && selectionStart === 0 && selectionEnd === 0) return { type: "insert-before" };
  return { type: "split", ...splitTitleAtSelection(title, selectionStart) };
}

export function shouldHandleMultiSelectionTab(
  event: { key: string; shiftKey?: boolean; defaultPrevented?: boolean },
  selectedCount: number,
  keyboardActive: boolean
) {
  return (
    event.key === "Tab" &&
    !event.shiftKey &&
    !event.defaultPrevented &&
    selectedCount > 1 &&
    keyboardActive
  );
}

export function shouldHandleOutlineSelectAll(
  event: {
    key: string;
    metaKey?: boolean;
    ctrlKey?: boolean;
    altKey?: boolean;
    shiftKey?: boolean;
    defaultPrevented?: boolean;
    isComposing?: boolean;
    nativeEvent?: { isComposing?: boolean; keyCode?: number };
  },
  editable: boolean,
  outlineTitle: boolean
) {
  return event.key.toLowerCase() === "a" && (event.metaKey || event.ctrlKey)
    && !event.altKey && !event.shiftKey && !event.defaultPrevented
    && !shouldIgnoreTextInputKeyDown(event) && (!editable || outlineTitle);
}

export function shouldHandleMultiSelectionDelete(
  event: {
    key: string;
    metaKey?: boolean;
    ctrlKey?: boolean;
    altKey?: boolean;
    defaultPrevented?: boolean;
    isComposing?: boolean;
  },
  selectedCount: number,
  keyboardActive: boolean,
  editable: boolean,
  outlineEditable: boolean
) {
  return (
    event.key === "Delete" &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.altKey &&
    !event.defaultPrevented &&
    !event.isComposing &&
    selectedCount > 1 &&
    keyboardActive &&
    (!editable || outlineEditable)
  );
}

export function shouldIgnoreTextInputKeyDown(event: {
  isComposing?: boolean;
  nativeEvent?: { isComposing?: boolean; keyCode?: number };
}) {
  return Boolean(event.isComposing || event.nativeEvent?.isComposing || event.nativeEvent?.keyCode === 229);
}

function isTitleCaretAtEdge(input: HTMLTextAreaElement, measure: HTMLElement | null, edge: "start" | "end"): boolean {
  if (input.selectionStart !== input.selectionEnd) return false;
  const cursor = input.selectionStart;
  if (!measure) return edge === "start" ? cursor === 0 : cursor === input.value.length;
  // Measure the actual visual line, including soft wrapping and trailing newlines.
  const mirror = measure.cloneNode(false) as HTMLElement;
  const first = document.createElement("span");
  const caret = document.createElement("span");
  const last = document.createElement("span");
  for (const marker of [first, caret, last]) marker.textContent = "\u200b";
  mirror.append(first, input.value.slice(0, cursor), caret, input.value.slice(cursor), last);
  measure.parentElement?.append(mirror);
  try {
    const caretTop = caret.getBoundingClientRect().top;
    const edgeTop = (edge === "start" ? first : last).getBoundingClientRect().top;
    return Math.abs(caretTop - edgeTop) < 1;
  } finally {
    mirror.remove();
  }
}

function getPreviewSelectionStart(container: HTMLElement, clientX: number, clientY: number, title: string) {
  const measuredOffset = getMeasuredTextOffset(container, clientX, clientY);
  if (measuredOffset !== null) return Math.max(0, Math.min(measuredOffset, title.length));

  const caret = getCaretFromPoint(container.ownerDocument, clientX, clientY);
  if (!caret || !container.contains(caret.node)) return title.length;
  const renderedOffset = getTextOffset(container, caret.node, caret.offset);
  if (renderedOffset === null) return title.length;
  return Math.max(0, Math.min(renderedOffset, title.length));
}

function getMeasuredTextOffset(root: Node, clientX: number, clientY: number) {
  const document = root.ownerDocument;
  if (!document) return null;

  let renderedOffset = 0;
  let bestOffset: number | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;

  const measure = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const textLength = node.textContent?.length ?? 0;
      for (let index = 0; index < textLength; index += 1) {
        const range = document.createRange();
        range.setStart(node, index);
        range.setEnd(node, index + 1);
        const rect = range.getBoundingClientRect();
        range.detach();
        if (rect.width === 0 && rect.height === 0) continue;

        const midpoint = rect.left + rect.width / 2;
        const candidateOffset = clientX <= midpoint ? renderedOffset + index : renderedOffset + index + 1;
        const verticalDistance = clientY < rect.top ? rect.top - clientY : clientY > rect.bottom ? clientY - rect.bottom : 0;
        const horizontalDistance =
          clientX < rect.left ? rect.left - clientX : clientX > rect.right ? clientX - rect.right : 0;
        const distance = verticalDistance * 1000 + horizontalDistance;
        if (distance < bestDistance) {
          bestDistance = distance;
          bestOffset = candidateOffset;
        }
      }
      renderedOffset += textLength;
      return;
    }

    for (const child of Array.from(node.childNodes)) measure(child);
  };

  measure(root);
  return bestOffset;
}

function getCaretFromPoint(document: Document, clientX: number, clientY: number) {
  const documentWithCaret = document as Document & {
    caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null;
    caretRangeFromPoint?: (x: number, y: number) => Range | null;
  };
  const position = documentWithCaret.caretPositionFromPoint?.(clientX, clientY);
  if (position) return { node: position.offsetNode, offset: position.offset };

  const range = documentWithCaret.caretRangeFromPoint?.(clientX, clientY);
  if (!range) return null;
  return { node: range.startContainer, offset: range.startOffset };
}

function getTextOffset(root: Node, target: Node, targetOffset: number) {
  let offset = 0;
  const visit = (node: Node): number | null => {
    if (node === target) {
      if (node.nodeType === Node.TEXT_NODE) {
        return offset + Math.min(targetOffset, node.textContent?.length ?? 0);
      }
      return offset + Array.from(node.childNodes)
        .slice(0, targetOffset)
        .reduce((length, child) => length + (child.textContent?.length ?? 0), 0);
    }
    if (node.nodeType === Node.TEXT_NODE) {
      offset += node.textContent?.length ?? 0;
      return null;
    }
    for (const child of Array.from(node.childNodes)) {
      const result = visit(child);
      if (result !== null) return result;
    }
    return null;
  };
  return visit(root);
}

async function readClipboardText() {
  try {
    const text = await navigator.clipboard?.readText();
    return text?.trim().replace(/\s+/g, " ") ?? "";
  } catch {
    return "";
  }
}

function nextTheme(theme: Theme): Theme {
  if (theme === "light") return "dark";
  if (theme === "dark") return "system";
  return "light";
}

function themeLabel(theme: Theme): string {
  if (theme === "light") return "Light";
  if (theme === "dark") return "Dark";
  return "System";
}

export function createWorkspaceRequestBody(
  selectedWorkspace: Pick<Workspace, "folderId" | "parentWorkspaceId"> | null | undefined,
  folderId?: string | null,
  parentWorkspaceId?: string | null
) {
  const nextParentWorkspaceId = parentWorkspaceId !== undefined
    ? parentWorkspaceId
    : folderId !== undefined
      ? null
      : selectedWorkspace?.parentWorkspaceId ?? null;
  return {
    name: "Untitled Workspace",
    icon: randomWorkspaceIcon(),
    folderId: nextParentWorkspaceId
      ? null
      : folderId !== undefined
        ? folderId
        : selectedWorkspace?.folderId ?? null,
    parentWorkspaceId: nextParentWorkspaceId
  };
}

export function nextWorkspaceIdAfterDelete(workspaces: Workspace[], deletedWorkspaceId: string): string {
  return workspaces.find(workspace =>
    workspace.id !== deletedWorkspaceId && !isWorkspaceDescendant(workspaces, workspace.id, deletedWorkspaceId)
  )?.id ?? "";
}

export function nextCollapsedWorkspaceFolderIds(current: Set<string>, folderId: string): Set<string> {
  const next = new Set(current);
  if (next.has(folderId)) {
    next.delete(folderId);
  } else {
    next.add(folderId);
  }
  return next;
}

export function nextCollapsedWorkspaceIds(current: Set<string>, workspaceId: string): Set<string> {
  const next = new Set(current);
  if (next.has(workspaceId)) {
    next.delete(workspaceId);
  } else {
    next.add(workspaceId);
  }
  return next;
}

function isEditableElement(target: EventTarget | null): boolean {
  return target instanceof HTMLElement && (
    target.matches("input, textarea, select") || target.isContentEditable
  );
}

function isOutlineEditableElement(target: EventTarget | null): boolean {
  return target instanceof HTMLElement && target.matches(".nodeTitle, .nodeDatePicker, .nodeNotes");
}

export function getOutlineHistoryShortcut(
  event: Pick<globalThis.KeyboardEvent, "key" | "metaKey" | "ctrlKey" | "shiftKey">,
  canUndo: boolean,
  canRedo: boolean,
  editable: boolean,
  outlineEditable: boolean
): "undo" | "redo" | null {
  if (editable && !outlineEditable) return null;
  const key = event.key.toLowerCase();
  const modifier = event.metaKey || event.ctrlKey;
  const undo = modifier && key === "z" && !event.shiftKey;
  const redo = modifier && ((key === "z" && event.shiftKey) || (key === "y" && event.ctrlKey));
  if (undo && (canUndo || outlineEditable)) return "undo";
  if (redo && canRedo) return "redo";
  return null;
}

function toError(setError: (message: string) => void) {
  return (error: unknown) => {
    setError(error instanceof Error ? error.message : "Unexpected error");
  };
}
